import React, { useState, useEffect } from "react";
import { motion, useMotionValue, useMotionTemplate, useScroll, useTransform, AnimatePresence } from "motion/react";
import { Cpu, Globe, BookOpen, Palette, Layers, Leaf, ArrowUpRight, Rocket, Wind, Terminal, Printer, Sparkles, Film, Users, Heart, History, BarChart3, ShieldCheck, ArrowRight, Mail, Smartphone, Hexagon, Briefcase, CheckCircle2, Box, Plane, ChevronDown, ChevronUp } from "lucide-react";
import { modulesData } from "./data/modulesData";
import { ModuleDetail } from "./components/ModuleDetail";
import { QefResourceCenter } from "./components/QefResourceCenter";
import { ContactModal } from "./components/ContactModal";

const I18nContext = React.createContext<{ t: (key: string) => string }>({ t: (k) => k });

const SpaceshipBackground = () => {
  const { scrollYProgress } = useScroll();
  
  // Transform values for a 3D-like parallax effect
  const rotateX = useTransform(scrollYProgress, [0, 1], [20, -20]);
  const rotateY = useTransform(scrollYProgress, [0, 1], [-10, 10]);
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "50%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0.15, 0.3, 0.3, 0.15]);

  return (
    <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
      <motion.div 
        style={{ 
          rotateX, 
          rotateY, 
          y, 
          opacity,
          perspective: 1200 
        }}
        className="absolute top-[-10%] md:top-[-20%] right-[-20%] md:right-[-10%] w-[120vw] md:w-[80vw] h-[120vh] md:h-[140vh] flex items-center justify-center"
      >
        {/* Large Wireframe Spaceship Structure */}
        <div className="relative w-full h-full">
          <svg viewBox="0 0 800 1200" className="w-full h-full text-white/[0.1]">
            <defs>
              <linearGradient id="spaceshipGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="currentColor" stopOpacity="0.2" />
                <stop offset="50%" stopColor="currentColor" stopOpacity="0.05" />
                <stop offset="100%" stopColor="currentColor" stopOpacity="0.2" />
              </linearGradient>
            </defs>
            <path 
              d="M400 50 L750 350 L750 850 L400 1150 L50 850 L50 350 Z" 
              fill="url(#spaceshipGrad)" 
              stroke="currentColor" 
              strokeWidth="1" 
            />
            <path d="M400 50 V1150" stroke="currentColor" strokeWidth="0.5" strokeDasharray="10 10" />
            <path d="M50 350 H750" stroke="currentColor" strokeWidth="0.5" strokeDasharray="10 10" />
            <path d="M50 850 H750" stroke="currentColor" strokeWidth="0.5" strokeDasharray="10 10" />
            
            {/* Turbine-like structures */}
            <circle cx="400" cy="450" r="180" fill="none" stroke="currentColor" strokeWidth="0.8" strokeDasharray="5 5" />
            <circle cx="400" cy="450" r="120" fill="none" stroke="currentColor" strokeWidth="0.4" />
            <circle cx="400" cy="750" r="180" fill="none" stroke="currentColor" strokeWidth="0.8" strokeDasharray="5 5" />
            <circle cx="400" cy="750" r="120" fill="none" stroke="currentColor" strokeWidth="0.4" />
            
            {/* Hexagonal Grid Overlay */}
            {[...Array(6)].map((_, i) => (
              <path 
                key={i}
                d={`M${100 + i * 120} 200 L${160 + i * 120} 230 L${160 + i * 120} 300 L${100 + i * 120} 330 L${40 + i * 120} 300 L${40 + i * 120} 230 Z`}
                fill="none"
                stroke="currentColor"
                strokeWidth="0.2"
                opacity="0.5"
              />
            ))}
          </svg>
          
          {/* Floating Glowing Nodes */}
          <motion.div 
            animate={{ 
              boxShadow: ["0 0 20px rgba(59,130,246,0.2)", "0 0 40px rgba(59,130,246,0.4)", "0 0 20px rgba(59,130,246,0.2)"],
              scale: [1, 1.2, 1]
            }}
            transition={{ duration: 3, repeat: Infinity }}
            className="absolute top-[35%] left-[48%] w-6 h-6 rounded-full bg-blue-500/30 blur-[2px]" 
          />
          <motion.div 
            animate={{ 
              boxShadow: ["0 0 20px rgba(16,185,129,0.2)", "0 0 40px rgba(16,185,129,0.4)", "0 0 20px rgba(16,185,129,0.2)"],
              scale: [1, 1.3, 1]
            }}
            transition={{ duration: 4, repeat: Infinity, delay: 1 }}
            className="absolute top-[65%] left-[52%] w-8 h-8 rounded-full bg-emerald-500/30 blur-[3px]" 
          />
        </div>
      </motion.div>
    </div>
  );
};

const HighTechCard = ({ children, className = "", spotlightColor = "rgba(255,255,255,0.1)", skills = [], onClick }: { children: React.ReactNode, className?: string, spotlightColor?: string, skills?: string[], onClick?: () => void }) => {
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const { t } = React.useContext(I18nContext);

  function handleMouseMove({ currentTarget, clientX, clientY }: React.MouseEvent) {
    const { left, top } = currentTarget.getBoundingClientRect();
    mouseX.set(clientX - left);
    mouseY.set(clientY - top);
  }

  return (
    <motion.div
      onMouseMove={handleMouseMove}
      onClick={onClick}
      whileHover={{ 
        y: -10,
        transition: { duration: 0.4, ease: "easeOut" }
      }}
      className={`group relative overflow-hidden rounded-3xl border border-white/10 bg-[#0A0A0A]/90 backdrop-blur-sm transition-all duration-500 hover:border-blue-500/50 hover:shadow-[0_0_50px_rgba(59,130,246,0.2)] ${className} ${onClick ? 'cursor-pointer' : ''}`}
    >
      <motion.div
        className="pointer-events-none absolute -inset-px opacity-0 transition duration-300 group-hover:opacity-100 z-10"
        style={{
          background: useMotionTemplate`
            radial-gradient(
              600px circle at ${mouseX}px ${mouseY}px,
              ${spotlightColor},
              transparent 80%
            )
          `,
        }}
      />
      <div className="relative z-20 h-full flex flex-col">
        {children}
        
        {skills.length > 0 && (
          <div className="absolute inset-0 bg-black/95 backdrop-blur-xl opacity-0 group-hover:opacity-100 transition-all duration-500 flex flex-col justify-center p-6 md:p-10 z-30 translate-y-8 group-hover:translate-y-0">
            <div className="mb-4 md:mb-8">
              <div className="text-[10px] md:text-[12px] font-bold uppercase tracking-[0.4em] text-blue-400 mb-1 md:mb-2">{t("Curriculum")}</div>
              <h4 className="text-white font-bold uppercase tracking-[0.1em] text-[18px] md:text-[20px] leading-tight">{t("Skills Learned")}</h4>
            </div>
            <div className="grid grid-cols-1 gap-2 md:gap-4">
              {skills.map((skill, i) => (
                <div 
                  key={i} 
                  className="text-white/90 text-[14px] md:text-[16px] flex items-center gap-2 md:gap-4 font-medium group-hover:translate-x-0 -translate-x-4 transition-transform duration-500"
                  style={{ transitionDelay: `${i * 40}ms` }}
                >
                  <div className="w-1 h-1 md:w-2 md:h-2 bg-blue-500 rounded-full shadow-[0_0_12px_rgba(59,130,246,0.8)] shrink-0" />
                  <span className="truncate">{skill}</span>
                </div>
              ))}
            </div>
            
            <div className="mt-6 md:mt-10 pt-4 md:pt-8 border-t border-white/10">
              <div className="flex items-center gap-2 md:gap-3 text-[11px] md:text-[14px] font-bold uppercase tracking-[0.2em] text-blue-400/80">
                <Sparkles className="w-3 md:w-4 h-3 md:h-4" />
                Industry Standard Certification
              </div>
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
};

const translations: Record<string, Record<'en' | 'zh', string>> = {
  "課程現正招生": { en: "Courses are now enrolling", zh: "課程現正招生" },
  "速速報名": { en: "Sign up quickly", zh: "速速報名" },
  "FAQ": { en: "FAQ", zh: "常見問題" },
  "Frequently Asked Questions": { en: "Frequently Asked Questions", zh: "常見問題" },
  "What age groups are your programs suitable for?": { en: "What age groups are your programs suitable for?", zh: "你們的課程適合哪些年齡層？" },
  "Our programs are designed for a wide range of ages, from primary school students (with parent-child co-creation options) to secondary students, young adults, and even seniors in our intergenerational streams.": { en: "Our programs are designed for a wide range of ages, from primary school students (with parent-child co-creation options) to secondary students, young adults, and even seniors in our intergenerational streams.", zh: "我們的課程專為不同年齡層設計，從小學生（提供親子共創選項）到中學生、年輕人，甚至跨代學習計劃中的長者都適合。" },
  "Do I need prior coding or AI experience?": { en: "Do I need prior coding or AI experience?", zh: "我需要有編程或 AI 的經驗嗎？" },
  "Not at all! Our beginner courses are designed to be accessible to everyone, regardless of technical background. We guide you step-by-step from the basics to advanced applications.": { en: "Not at all! Our beginner courses are designed to be accessible to everyone, regardless of technical background. We guide you step-by-step from the basics to advanced applications.", zh: "完全不需要！我們的初學者課程專為所有人設計，無論技術背景如何。我們將循序漸進地引導您從基礎到進階應用。" },
  "Are your programs available for schools outside of Hong Kong?": { en: "Are your programs available for schools outside of Hong Kong?", zh: "你們的課程適用於香港以外的學校嗎？" },
  "Currently, our primary focus is on schools and organizations within Hong Kong, but we are open to discussing virtual partnerships or licensing our curriculum internationally. Please contact us for more details.": { en: "Currently, our primary focus is on schools and organizations within Hong Kong, but we are open to discussing virtual partnerships or licensing our curriculum internationally. Please contact us for more details.", zh: "目前，我們主要專注於香港的學校和機構，但我們對討論虛擬合作夥伴關係或在國際上授權我們的課程持開放態度。請聯絡我們了解更多詳情。" },
  "How do I enroll in a course?": { en: "How do I enroll in a course?", zh: "我該如何報名課程？" },
  "You can enroll directly through our Linktree for family and individual courses, or contact us via the 'Book Now' or 'Partner With Us' buttons for school and NGO programs.": { en: "You can enroll directly through our Linktree for family and individual courses, or contact us via the 'Book Now' or 'Partner With Us' buttons for school and NGO programs.", zh: "您可以直接透過我們的 Linktree 報名家庭和個人課程，或透過「立即預約」或「與我們合作」按鈕聯絡我們以了解學校和非政府組織計劃。" },
  "Book Consultation": { en: "Book Consultation", zh: "預約諮詢" },
  "Book": { en: "Book", zh: "預約" },
  "DESIGN. BUILD.": { en: "DESIGN. BUILD.", zh: "設計. 構建." },
  "SHIP THE FUTURE.": { en: "SHIP THE FUTURE.", zh: "創造未來." },
  "Hong Kong’s premier ArtTech partner providing turnkey EdTech curricula.": { en: "Hong Kong’s premier ArtTech partner providing turnkey EdTech curricula.", zh: "香港首屈一指的藝術科技合作夥伴，提供一站式教育科技課程。" },
  "For Schools": { en: "For Schools", zh: "學校方案" },
  "For Families": { en: "For Families", zh: "家庭方案" },
  "For NGOs": { en: "For NGOs", zh: "非政府組織方案" },
  "For NGOs and Associations": { en: "For NGOs and Associations", zh: "非政府組織及協會方案" },
  "For Families and Individuals": { en: "For Families and Individuals", zh: "家庭及個人方案" },
  "University-Backed Programs": { en: "University-Backed Programs", zh: "大學支持計劃" },
  "Bridge the": { en: "Bridge the", zh: "跨越" },
  "Tech Gap": { en: "Tech Gap", zh: "科技鴻溝" },
  "Trusted by schools, associations, and NGOs, and guided by an advisory board of university professors. We turn screen time into family co-creation.": { en: "Trusted by schools, associations, and NGOs, and guided by an advisory board of university professors. We turn screen time into family co-creation.", zh: "深受學校、協會和非政府組織信任，並由大學教授顧問委員會指導。我們將屏幕時間轉化為家庭共創。" },
  "Book Now": { en: "Book Now", zh: "立即預約" },
  "Inquire Now": { en: "Inquire Now", zh: "立即查詢" },
  "AI Beginner Courses": { en: "AI Beginner Courses", zh: "AI 初學者課程" },
  "Step-by-step foundational learning": { en: "Step-by-step foundational learning", zh: "循序漸進的基礎學習" },
  "Master generative AI tools": { en: "Master generative AI tools", zh: "掌握生成式 AI 工具" },
  "Boost daily productivity": { en: "Boost daily productivity", zh: "提升日常生產力" },
  "Parent-Child AI Co-Creation": { en: "Parent-Child AI Co-Creation", zh: "親子 AI 共創" },
  "Interactive family workshops": { en: "Interactive family workshops", zh: "互動家庭工作坊" },
  "Collaborate on stories & art": { en: "Collaborate on stories & art", zh: "合作創作故事與藝術" },
  "Turn screen time into bonding": { en: "Turn screen time into bonding", zh: "將屏幕時間轉化為親子時光" },
  "AI to Object Class": { en: "AI to Object Class", zh: "AI 實物化課程" },
  "Digital AI to physical objects": { en: "Digital AI to physical objects", zh: "數碼 AI 轉化為實體物品" },
  "3D printing & laser cutting": { en: "3D printing & laser cutting", zh: "3D 打印與激光切割" },
  "Hands-on maker experience": { en: "Hands-on maker experience", zh: "動手創客體驗" },
  "Drone Class": { en: "Drone Class", zh: "無人機課程" },
  "Learn to program & fly drones": { en: "Learn to program & fly drones", zh: "學習編程與飛行無人機" },
  "Combine coding with real-world": { en: "Combine coding with real-world", zh: "將編碼與現實世界結合" },
  "Exciting aerial challenges": { en: "Exciting aerial challenges", zh: "刺激的空中挑戰" },
  "Guided by": { en: "Guided by", zh: "指導機構" },
  "University Professors": { en: "University Professors", zh: "大學教授" },
  "Strategic Vision": { en: "Strategic Vision", zh: "戰略願景" },
  "\"We transform students from passive consumers into multi-domain tech creators.\"": { en: "\"We transform students from passive consumers into multi-domain tech creators.\"", zh: "「我們將學生從被動的消費者轉變為多領域的科技創造者。」" },
  "Empowering the next generation to not just use technology, but to architect the future through creative mastery.": { en: "Empowering the next generation to not just use technology, but to architect the future through creative mastery.", zh: "賦予下一代能力，不僅僅是使用科技，而是透過創意的掌握來構建未來。" },
  "STATUS: ACTIVE": { en: "STATUS: ACTIVE", zh: "狀態：活躍" },
  "Pedagogical Core": { en: "Pedagogical Core", zh: "教學核心" },
  "Productive Failure as a Catalyst for Innovation.": { en: "Productive Failure as a Catalyst for Innovation.", zh: "將建設性失敗作為創新的催化劑。" },
  "Our pedagogy focuses on learning through experimentation, iteration, and high-stakes problem solving. We believe that true mastery comes from navigating the space between what you know and what you're building.": { en: "Our pedagogy focuses on learning through experimentation, iteration, and high-stakes problem solving. We believe that true mastery comes from navigating the space between what you know and what you're building.", zh: "我們的教學法側重於透過實驗、迭代和高風險問題解決來學習。我們相信，真正的精通來自於在已知和正在構建的事物之間導航。" },
  "METHOD: ITERATIVE": { en: "METHOD: ITERATIVE", zh: "方法：迭代" },
  "Academic Trust": { en: "Academic Trust", zh: "學術信任" },
  "Rigorous Standards Guided by Expert Authority.": { en: "Rigorous Standards Guided by Expert Authority.", zh: "由專家權威指導的嚴格標準。" },
  "Trusted by schools, associations, and NGOs, and guided by an advisory board of university professors. Our curriculum is built on rigorous academic standards and real-world industry practices.": { en: "Trusted by schools, associations, and NGOs, and guided by an advisory board of university professors. Our curriculum is built on rigorous academic standards and real-world industry practices.", zh: "深受學校、協會和非政府組織信任，並由大學教授顧問委員會指導。我們的課程建立在嚴格的學術標準和現實世界的行業實踐之上。" },
  "VERIFIED: QEF": { en: "VERIFIED: QEF", zh: "認證：優質教育基金" },
  "Empower Schools": { en: "Empower Schools", zh: "賦能學校" },
  "We provide turnkey EdTech curricula, hardware, and expert support to help schools secure government grants ranging from ": { en: "We provide turnkey EdTech curricula, hardware, and expert support to help schools secure government grants ranging from ", zh: "我們提供一站式教育科技課程、硬件和專家支援，幫助學校獲得從 " },
  "HKD 500k to 2M": { en: "HKD 500k to 2M", zh: "50萬至200萬港元" },
  ". Our mission is to modernize the classroom experience with seamless integration.": { en: ". Our mission is to modernize the classroom experience with seamless integration.", zh: " 的政府資助。我們的使命是透過無縫整合來現代化課堂體驗。" },
  "Explore Curricula": { en: "Explore Curricula", zh: "探索課程" },
  "Empower Communities": { en: "Empower Communities", zh: "賦能社區" },
  "We partner with NGOs and associations to deliver high-impact digital literacy and co-creation programs. Our initiatives bridge the digital divide and foster intergenerational connection.": { en: "We partner with NGOs and associations to deliver high-impact digital literacy and co-creation programs. Our initiatives bridge the digital divide and foster intergenerational connection.", zh: "我們與非政府組織和協會合作，提供高影響力的數碼素養和共創計劃。我們的倡議旨在跨越數碼鴻溝並促進跨代聯繫。" },
  "AI Literacy Courses": { en: "AI Literacy Courses", zh: "AI 素養課程" },
  "Comprehensive AI training programs designed to demystify artificial intelligence. We equip your community with the foundational knowledge and practical skills needed to navigate and thrive in an AI-driven world.": { en: "Comprehensive AI training programs designed to demystify artificial intelligence. We equip your community with the foundational knowledge and practical skills needed to navigate and thrive in an AI-driven world.", zh: "旨在揭開人工智能神秘面紗的全面 AI 培訓計劃。我們為您的社區配備在 AI 驅動的世界中導航和發展所需的基礎知識和實用技能。" },
  "Co-Creation Projects": { en: "Co-Creation Projects", zh: "共創項目" },
  "Collaborative initiatives where technology meets community needs. We partner with NGOs to design and implement tech-driven solutions that address real-world challenges, fostering innovation from the ground up.": { en: "Collaborative initiatives where technology meets community needs. We partner with NGOs to design and implement tech-driven solutions that address real-world challenges, fostering innovation from the ground up.", zh: "科技滿足社區需求的合作倡議。我們與非政府組織合作設計和實施科技驅動的解決方案，應對現實世界的挑戰，從基層培養創新。" },
  "Intergenerational Stream": { en: "Intergenerational Stream", zh: "跨代交流" },
  "Bridging the age gap through shared technological experiences. Our programs bring youth and seniors together, promoting mutual understanding, reducing social isolation, and preserving cultural heritage through digital mediums.": { en: "Bridging the age gap through shared technological experiences. Our programs bring youth and seniors together, promoting mutual understanding, reducing social isolation, and preserving cultural heritage through digital mediums.", zh: "透過共享科技體驗跨越年齡鴻溝。我們的計劃將青年和長者聚集在一起，促進相互理解，減少社會孤立，並透過數碼媒介保護文化遺產。" },
  "Group Training for Professionals": { en: "Group Training for Professionals", zh: "專業人士小組培訓" },
  "Tailored upskilling workshops for NGO staff and social workers. We provide hands-on training in the latest digital tools and AI applications, empowering your team to work more efficiently and amplify their impact.": { en: "Tailored upskilling workshops for NGO staff and social workers. We provide hands-on training in the latest digital tools and AI applications, empowering your team to work more efficiently and amplify their impact.", zh: "為非政府組織員工和社會工作者量身定制的技能提升工作坊。我們提供最新數碼工具和 AI 應用的實踐培訓，使您的團隊能夠更高效地工作並擴大其影響力。" },
  "Impact Metrics": { en: "Impact Metrics", zh: "影響力指標" },
  "Measurable Community Impact": { en: "Measurable Community Impact", zh: "可衡量的社區影響" },
  "Young-old Empowered": { en: "Young-old Empowered", zh: "賦能初老長者" },
  "Guided to confidently publish AI memoirs and digital art.": { en: "Guided to confidently publish AI memoirs and digital art.", zh: "引導自信地出版 AI 回憶錄和數碼藝術。" },
  "Youth Mentors": { en: "Youth Mentors", zh: "青年導師" },
  "A growing network trained to bridge the digital divide.": { en: "A growing network trained to bridge the digital divide.", zh: "一個不斷發展的網絡，受訓以跨越數碼鴻溝。" },
  "Loneliness Scale": { en: "Loneliness Scale", zh: "孤獨感量表" },
  "Scientifically tracked social inclusion and isolation reduction.": { en: "Scientifically tracked social inclusion and isolation reduction.", zh: "科學追蹤社會包容和減少孤立。" },
  "Flagship Project Example & Outcomes": { en: "Flagship Project Example & Outcomes", zh: "旗艦項目範例與成果" },
  "Digital Heritage: Recreating History with AI": { en: "Digital Heritage: Recreating History with AI", zh: "數碼遺產：用 AI 重塑歷史" },
  "Students used AI video generation and voice cloning to recreate the life of Hilton Cheong-Leen (\"The Father of HK Hawkers\").": { en: "Students used AI video generation and voice cloning to recreate the life of Hilton Cheong-Leen (\"The Father of HK Hawkers\").", zh: "學生們使用 AI 視頻生成和語音克隆技術重塑了張有興（「香港小販之父」）的一生。" },
  "Outcome:": { en: "Outcome:", zh: "成果：" },
  " A stunning, high-value humanities PBL (Project-Based Learning) portfolio piece that preserves local culture.": { en: " A stunning, high-value humanities PBL (Project-Based Learning) portfolio piece that preserves local culture.", zh: " 一個令人驚嘆的、高價值的人文學科 PBL（專題式學習）作品集，保護了本土文化。" },
  "We have guided over ": { en: "We have guided over ", zh: "我們已引導超過 " },
  "40 young-olds": { en: "40 young-olds", zh: "40 位初老長者" },
  " to do the chapter on their own to combine into a mini-film.": { en: " to do the chapter on their own to combine into a mini-film.", zh: " 獨立完成章節，並組合成一部微電影。" },
  "Intergenerational Connection:": { en: "Intergenerational Connection:", zh: "跨代聯繫：" },
  " Fostered meaningful collaboration between youth and elders through shared digital creation.": { en: " Fostered meaningful collaboration between youth and elders through shared digital creation.", zh: " 透過共享數碼創作，促進了青年與長者之間有意義的合作。" },
  "Digital Literacy:": { en: "Digital Literacy:", zh: "數碼素養：" },
  " Empowered seniors with practical AI skills, reducing the digital divide and enhancing social inclusion.": { en: " Empowered seniors with practical AI skills, reducing the digital divide and enhancing social inclusion.", zh: " 賦予長者實用的 AI 技能，減少數碼鴻溝並增強社會包容性。" },
  "Phase 01": { en: "Phase 01", zh: "階段 01" },
  "Phase 02": { en: "Phase 02", zh: "階段 02" },
  "Phase 03": { en: "Phase 03", zh: "階段 03" },
  "Prepare Students": { en: "Prepare Students", zh: "裝備學生" },
  "We help students build elite ": { en: "We help students build elite ", zh: "我們幫助學生建立頂尖的 " },
  "STEAM portfolios": { en: "STEAM portfolios", zh: "STEAM 作品集" },
  " featuring real-world prototypes. Our mentorship is designed to significantly enhance university admissions for competitive pathways like ": { en: " featuring real-world prototypes. Our mentorship is designed to significantly enhance university admissions for competitive pathways like ", zh: "，其中包含現實世界的原型。我們的導師計劃旨在顯著提高競爭激烈途徑的大學錄取率，例如 " },
  "JUPAS": { en: "JUPAS", zh: "聯招 (JUPAS)" },
  " and the ": { en: " and the ", zh: " 和 " },
  "Ivy League": { en: "Ivy League", zh: "常春藤盟校" },
  ".": { en: ".", zh: "。" },
  "Drive Social Impact": { en: "Drive Social Impact", zh: "推動社會影響" },
  "We bridge the digital divide through social inclusion projects. From training seniors in AI to masterminding ": { en: "We bridge the digital divide through social inclusion projects. From training seniors in AI to masterminding ", zh: "我們透過社會包容項目跨越數碼鴻溝。從培訓長者使用 AI 到策劃 " },
  "ESG-aligned sustainability initiatives": { en: "ESG-aligned sustainability initiatives", zh: "符合 ESG 標準的可持續發展倡議" },
  ", we ensure technology serves the entire community.": { en: ", we ensure technology serves the entire community.", zh: "，我們確保科技服務於整個社區。" },
  "Elite STEAM Curricula": { en: "Elite STEAM Curricula", zh: "精英 STEAM 課程" },
  "Mastering": { en: "Mastering", zh: "掌握" },
  "The Future": { en: "The Future", zh: "未來" },
  "\"We don't just teach technology; we build the architects of tomorrow's digital landscape.\"": { en: "\"We don't just teach technology; we build the architects of tomorrow's digital landscape.\"", zh: "「我們不僅教授科技；我們培養明日數碼領域的建築師。」" },
  "Available for QEF School Licensing & 12-Week Bootcamps": { en: "Available for QEF School Licensing & 12-Week Bootcamps", zh: "提供優質教育基金學校授權及 12 週訓練營" },
  "License Now": { en: "License Now", zh: "立即授權" },
  "For NGOs & Associations": { en: "For NGOs & Associations", zh: "為非政府組織和協會而設" },
  "AI Literacy & Co-Creation": { en: "AI Literacy & Co-Creation", zh: "AI 素養與共創" },
  "Empowering": { en: "Empowering", zh: "賦能" },
  "Communities": { en: "Communities", zh: "社區" },
  "Partner with us to bring AI literacy, co-creation projects, intergenerational programs, and professional group training to your community.": { en: "Partner with us to bring AI literacy, co-creation projects, intergenerational programs, and professional group training to your community.", zh: "與我們合作，將 AI 素養、共創項目、跨代計劃和專業小組培訓帶入您的社區。" },
  "Social Inclusion Certified": { en: "Social Inclusion Certified", zh: "社會包容認證" },
  "TRACKED BY UCLA LONELINESS SCALE TO REDUCE SOCIAL ISOLATION": { en: "TRACKED BY UCLA LONELINESS SCALE TO REDUCE SOCIAL ISOLATION", zh: "透過加州大學洛杉磯分校孤獨感量表追蹤以減少社會孤立" },
  "Partnership": { en: "Partnership", zh: "合作夥伴關係" },
  "Take Your Next Step With Us": { en: "Take Your Next Step With Us", zh: "與我們一起邁出下一步" },
  "Take Your ": { en: "Take Your ", zh: "與我們一起邁出" },
  "Next Step": { en: "Next Step", zh: "下一步" },
  " With Us": { en: " With Us", zh: "" },
  "Schools": { en: "Schools", zh: "學校" },
  "Book a Free QEF Strategy Consultation to elevate your curriculum.": { en: "Book a Free QEF Strategy Consultation to elevate your curriculum.", zh: "預約免費優質教育基金策略諮詢，提升您的課程。" },
  "Parents": { en: "Parents", zh: "家長" },
  "Enroll in Parent-Child AI & Drone Bootcamps for a future-ready family.": { en: "Enroll in Parent-Child AI & Drone Bootcamps for a future-ready family.", zh: "報名參加親子 AI 和無人機訓練營，為未來做好準備。" },
  "Enroll Today": { en: "Enroll Today", zh: "立即報名" },
  "Associations": { en: "Associations", zh: "協會" },
  "Partner on CSR & SIE Fund Projects to create lasting social impact.": { en: "Partner on CSR & SIE Fund Projects to create lasting social impact.", zh: "在企業社會責任和社會創新及創業發展基金項目上合作，創造持久的社會影響。" },
  "Our Hubs": { en: "Our Hubs", zh: "我們的中心" },
  "Kwun Tong": { en: "Kwun Tong", zh: "觀塘" },
  "AI Hub & Training Center": { en: "AI Hub & Training Center", zh: "AI 中心及培訓中心" },
  "Yuen Long": { en: "Yuen Long", zh: "元朗" },
  "Community IoT & ArtTech Incubator": { en: "Community IoT & ArtTech Incubator", zh: "社區物聯網及藝術科技孵化器" },
  "Connect": { en: "Connect", zh: "聯絡我們" },
  "Programs": { en: "Programs", zh: "課程計劃" },
  "© 2026 CREATECH INNOVATION ACADEMY. ALL RIGHTS RESERVED.": { en: "© 2026 CREATECH INNOVATION ACADEMY. ALL RIGHTS RESERVED.", zh: "© 2026 創科創新學院。版權所有。" },
  "Privacy Policy": { en: "Privacy Policy", zh: "隱私政策" },
  "Terms of Service": { en: "Terms of Service", zh: "服務條款" },
  "Curriculum": { en: "Curriculum", zh: "課程" },
  "Skills Learned": { en: "Skills Learned", zh: "學習技能" },
  "Core Protocol": { en: "Core Protocol", zh: "核心協議" },
  "Implementation": { en: "Implementation", zh: "實施" },
  "Discovery": { en: "Discovery", zh: "探索" },
  "Strategy": { en: "Strategy", zh: "策略" },
  "Launch": { en: "Launch", zh: "啟動" },
  "UCLA": { en: "UCLA", zh: "加州大學洛杉磯分校" },
  "Identify ArtTech goals through expert consultation.": { en: "Identify ArtTech goals through expert consultation.", zh: "透過專家諮詢確定藝術科技目標。" },
  "Receive a custom EdTech roadmap or STEAM plan.": { en: "Receive a custom EdTech roadmap or STEAM plan.", zh: "獲得定制的教育科技路線圖或 STEAM 計劃。" },
  "Implement the curriculum or start building prototypes.": { en: "Implement the curriculum or start building prototypes.", zh: "實施課程或開始建立原型。" },
  "Category:": { en: "Category:", zh: "類別：" },
  "Module:": { en: "Module:", zh: "模組：" },
  "Name": { en: "Name", zh: "姓名" },
  "John Doe": { en: "John Doe", zh: "陳大文" },
  "Email": { en: "Email", zh: "電郵" },
  "john@example.com": { en: "john@example.com", zh: "john@example.com" },
  "Phone Number": { en: "Phone Number", zh: "電話號碼" },
  "+852 1234 5678": { en: "+852 1234 5678", zh: "+852 1234 5678" },
  "What would you like to do?": { en: "What would you like to do?", zh: "您想做什麼？" },
  "Tell us about your goals or how we can help...": { en: "Tell us about your goals or how we can help...", zh: "告訴我們您的目標或我們如何提供幫助..." },
  "Submit Request": { en: "Submit Request", zh: "提交請求" },
  "Thank you for your interest! We will get back to you soon.": { en: "Thank you for your interest! We will get back to you soon.", zh: "感謝您的關注！我們將盡快與您聯繫。" },
  "Space Module QEF Draft": { en: "Space Module QEF Draft", zh: "太空模組優質教育基金草案" },
  "IoT Maker Lab Budget Template": { en: "IoT Maker Lab Budget Template", zh: "物聯網創客實驗室預算範本" },
  "Vibe Coding Curriculum Map": { en: "Vibe Coding Curriculum Map", zh: "Vibe 編程課程地圖" },
  "Drone Missions Risk Assessment": { en: "Drone Missions Risk Assessment", zh: "無人機任務風險評估" },
  "Educator Support": { en: "Educator Support", zh: "教育工作者支援" },
  "QEF Resource Center": { en: "QEF Resource Center", zh: "優質教育基金資源中心" },
  "CreaTech Academy modules are meticulously designed to align with EDB STEAM and Junior Secondary AI Curriculum guidelines. We provide the documentation you need to secure Quality Education Fund (QEF) backing.": { en: "CreaTech Academy modules are meticulously designed to align with EDB STEAM and Junior Secondary AI Curriculum guidelines. We provide the documentation you need to secure Quality Education Fund (QEF) backing.", zh: "創科創新學院的模組經過精心設計，符合教育局 STEAM 和初中人工智能課程指引。我們提供您確保獲得優質教育基金 (QEF) 支持所需的文件。" },
  "Need a Custom Proposal?": { en: "Need a Custom Proposal?", zh: "需要定制提案？" },
  "Schedule a meeting with our curriculum specialists. We'll help you tailor a QEF proposal that perfectly fits your school's unique needs and student demographics.": { en: "Schedule a meeting with our curriculum specialists. We'll help you tailor a QEF proposal that perfectly fits your school's unique needs and student demographics.", zh: "安排與我們的課程專家會面。我們將幫助您量身定制完全符合貴校獨特需求和學生人口統計的 QEF 提案。" },
  "Back to Programs": { en: "Back to Programs", zh: "返回課程計劃" },
  "The Vision": { en: "The Vision", zh: "願景" },
  "Toolkit & Ecosystem": { en: "Toolkit & Ecosystem", zh: "工具包和生態系統" },
  "Tech Stack": { en: "Tech Stack", zh: "技術棧" },
  "Hardware": { en: "Hardware", zh: "硬件" },
  "Student Deliverable": { en: "Student Deliverable", zh: "學生成果" },
  "QEF Evaluation Metrics": { en: "QEF Evaluation Metrics", zh: "優質教育基金評估指標" },
  "Quantitative": { en: "Quantitative", zh: "定量" },
  "Qualitative": { en: "Qualitative", zh: "定性" },
  "Book This Module": { en: "Book This Module", zh: "預約此模組" },
  // Space Education
  "Space Education Powered by AI and Data Science": { en: "Space Education Powered by AI and Data Science", zh: "由 AI 與數據科學驅動的太空教育" },
  "ArtTech & Young CEO (Artech and Design Thinking)": { en: "ArtTech & Young CEO (Artech and Design Thinking)", zh: "藝術科技與青年 CEO (藝術科技與設計思維)" },
  "AI Movie & Director Program (AI Art and AI-assisted creation)": { en: "AI Movie & Director Program (AI Art and AI-assisted creation)", zh: "AI 電影與導演計劃 (AI 藝術與 AI 輔助創作)" },
  "Space Education & Data Science": { en: "Space Education & Data Science", zh: "太空教育與數據科學" },
  "Transform your classroom into mission control. Students analyze real satellite data and design space habitats using advanced AI, preparing them for the booming aerospace industry.": { en: "Transform your classroom into mission control. Students analyze real satellite data and design space habitats using advanced AI, preparing them for the booming aerospace industry.", zh: "將您的教室變成任務控制中心。學生使用先進的 AI 分析真實衛星數據並設計太空棲息地，為蓬勃發展的航空航天業做好準備。" },
  "A comprehensive data analysis report on exoplanet habitability and a 3D-modeled space settlement prototype.": { en: "A comprehensive data analysis report on exoplanet habitability and a 3D-modeled space settlement prototype.", zh: "關於系外行星宜居性的綜合數據分析報告和 3D 建模的太空定居點原型。" },
  "85% of students demonstrate proficiency in basic Python data analysis; 100% complete a capstone project.": { en: "85% of students demonstrate proficiency in basic Python data analysis; 100% complete a capstone project.", zh: "85% 的學生表現出對基本 Python 數據分析的熟練程度；100% 完成頂點項目。" },
  "Enhanced critical thinking, spatial reasoning, and understanding of complex datasets in real-world contexts.": { en: "Enhanced critical thinking, spatial reasoning, and understanding of complex datasets in real-world contexts.", zh: "增強批判性思維、空間推理以及在現實環境中對複雜數據集的理解。" },

  // Drone Missions
  "The Low-Altitude Economy: Drone Missions": { en: "The Low-Altitude Economy: Drone Missions", zh: "低空經濟：無人機任務" },
  "Equip students for the future of logistics and surveillance. They will program autonomous drone flight paths and use computer vision to analyze aerial data.": { en: "Equip students for the future of logistics and surveillance. They will program autonomous drone flight paths and use computer vision to analyze aerial data.", zh: "為學生裝備未來的物流和監控技能。他們將編程自主無人機飛行路徑，並使用計算機視覺分析航空數據。" },
  "An autonomous drone mission that successfully navigates an obstacle course and identifies specific ground targets.": { en: "An autonomous drone mission that successfully navigates an obstacle course and identifies specific ground targets.", zh: "成功導航障礙訓練場並識別特定地面目標的自主無人機任務。" },
  "90% of students successfully program a multi-waypoint autonomous flight; 80% implement basic object detection.": { en: "90% of students successfully program a multi-waypoint autonomous flight; 80% implement basic object detection.", zh: "90% 的學生成功編程多航點自主飛行；80% 實現基本對象檢測。" },
  "Improved spatial awareness, algorithmic thinking, and understanding of aviation safety and ethics.": { en: "Improved spatial awareness, algorithmic thinking, and understanding of aviation safety and ethics.", zh: "提高空間意識、算法思維以及對航空安全和倫理的理解。" },

  // Vibe Coding
  "CreaTech AI Prompt Lab: Vibe Coding": { en: "CreaTech AI Prompt Lab: Vibe Coding", zh: "CreaTech AI 提示實驗室：Vibe 編程" },
  "Unlock the creative potential of generative AI. Students learn to craft precise prompts to generate art, music, and stories, blending technology with artistic expression.": { en: "Unlock the creative potential of generative AI. Students learn to craft precise prompts to generate art, music, and stories, blending technology with artistic expression.", zh: "釋放生成式 AI 的創造潛力。學生學習製作精確的提示來生成藝術、音樂和故事，將技術與藝術表達相結合。" },
  "A multimedia digital portfolio featuring AI-assisted artwork, a short story, and an original soundtrack.": { en: "A multimedia digital portfolio featuring AI-assisted artwork, a short story, and an original soundtrack.", zh: "以 AI 輔助藝術品、短篇小說和原創配樂為特色的多媒體數字作品集。" },
  "100% of students create a portfolio with at least 3 distinct AI-generated media types.": { en: "100% of students create a portfolio with at least 3 distinct AI-generated media types.", zh: "100% 的學生創建包含至少 3 種不同 AI 生成媒體類型的作品集。" },
  "Development of 'prompt engineering' skills, creative problem solving, and digital literacy.": { en: "Development of 'prompt engineering' skills, creative problem solving, and digital literacy.", zh: "培養「提示工程」技能、創造性解決問題的能力和數字素養。" },

  // IoT Maker Lab
  "Text-to-Object: IoT Maker Lab": { en: "Text-to-Object: IoT Maker Lab", zh: "文本到對象：物聯網創客實驗室" },
  "Bridge the digital and physical worlds. Students use AI to generate 3D models from text descriptions, then bring them to life with 3D printing and IoT sensors.": { en: "Bridge the digital and physical worlds. Students use AI to generate 3D models from text descriptions, then bring them to life with 3D printing and IoT sensors.", zh: "連接數字和物理世界。學生使用 AI 從文本描述生成 3D 模型，然後通過 3D 打印和物聯網傳感器將其變為現實。" },
  "A functional, 3D-printed smart device that responds to environmental stimuli.": { en: "A functional, 3D-printed smart device that responds to environmental stimuli.", zh: "一個響應環境刺激的功能性 3D 打印智能設備。" },
  "95% of students successfully print a 3D object; 85% integrate at least one working sensor.": { en: "95% of students successfully print a 3D object; 85% integrate at least one working sensor.", zh: "95% 的學生成功打印 3D 對象；85% 集成至少一個工作傳感器。" },
  "Hands-on experience with rapid prototyping, understanding of IoT concepts, and iterative design thinking.": { en: "Hands-on experience with rapid prototyping, understanding of IoT concepts, and iterative design thinking.", zh: "快速原型製作的實踐經驗、對物聯網概念的理解和迭代設計思維。" },

  // Tech Entrepreneurship
  "Young CEO: Tech Entrepreneurship": { en: "Young CEO: Tech Entrepreneurship", zh: "青年 CEO：科技創業" },
  "Empower the next generation of innovators. Students identify real-world problems, develop tech-based solutions, and pitch their startup ideas to a panel of 'investors'.": { en: "Empower the next generation of innovators. Students identify real-world problems, develop tech-based solutions, and pitch their startup ideas to a panel of 'investors'.", zh: "賦能下一代創新者。學生識別現實世界的問題，開發基於技術的解決方案，並向「投資者」小組推銷他們的創業想法。" },
  "A functional app prototype, a comprehensive business plan, and a polished pitch deck.": { en: "A functional app prototype, a comprehensive business plan, and a polished pitch deck.", zh: "功能性應用程序原型、全面的商業計劃和精美的推介演示文稿。" },
  "100% of teams develop a working prototype; 90% deliver a complete pitch presentation.": { en: "100% of teams develop a working prototype; 90% deliver a complete pitch presentation.", zh: "100% 的團隊開發出工作原型；90% 發表完整的推介演講。" },
  "Fostering entrepreneurial mindset, public speaking skills, teamwork, and business acumen.": { en: "Fostering entrepreneurial mindset, public speaking skills, teamwork, and business acumen.", zh: "培養創業思維、公開演講技巧、團隊合作和商業敏銳度。" },

  // Digital Heritage
  "Digital Heritage & Intergenerational Co-creation": { en: "Digital Heritage & Intergenerational Co-creation", zh: "數字遺產與代際共創" },
  "Preserve history through technology. Students collaborate with elders to digitize local heritage, creating interactive AR experiences and digital archives.": { en: "Preserve history through technology. Students collaborate with elders to digitize local heritage, creating interactive AR experiences and digital archives.", zh: "通過技術保存歷史。學生與長者合作將當地遺產數字化，創建交互式 AR 體驗和數字檔案。" },
  "An interactive AR exhibition showcasing digitized local history and oral histories.": { en: "An interactive AR exhibition showcasing digitized local history and oral histories.", zh: "展示數字化當地歷史和口述歷史的交互式 AR 展覽。" },
  "Each student digitizes at least 3 historical artifacts/stories; 100% participate in the final exhibition.": { en: "Each student digitizes at least 3 historical artifacts/stories; 100% participate in the final exhibition.", zh: "每位學生至少數字化 3 件歷史文物/故事；100% 參與最終展覽。" },
  "Enhanced empathy, historical awareness, community engagement, and digital storytelling skills.": { en: "Enhanced empathy, historical awareness, community engagement, and digital storytelling skills.", zh: "增強同理心、歷史意識、社區參與和數字講故事技能。" },

  "Ages 14-18": { en: "Ages 14-18", zh: "14-18 歲" },
  "Ages 12-18": { en: "Ages 12-18", zh: "12-18 歲" },
  "Ages 7-18": { en: "Ages 7-18", zh: "7-18 歲" },
  "Ages 7-15": { en: "Ages 7-15", zh: "7-15 歲" },

  // Tech Stack & Hardware
  "Python": { en: "Python", zh: "Python" },
  "TensorFlow": { en: "TensorFlow", zh: "TensorFlow" },
  "Generative AI (Spatial Design)": { en: "Generative AI (Spatial Design)", zh: "生成式 AI (空間設計)" },
  "Data Visualization": { en: "Data Visualization", zh: "數據可視化" },
  "High-Performance Laptops": { en: "High-Performance Laptops", zh: "高性能筆記本電腦" },
  "VR Headsets (Optional)": { en: "VR Headsets (Optional)", zh: "VR 頭顯 (可選)" },
  "Microcontrollers": { en: "Microcontrollers", zh: "微控制器" },
  "OpenCV": { en: "OpenCV", zh: "OpenCV" },
  "Flight Control Software": { en: "Flight Control Software", zh: "飛行控制軟件" },
  "Block-based Coding": { en: "Block-based Coding", zh: "積木編程" },
  "Programmable Drones": { en: "Programmable Drones", zh: "可編程無人機" },
  "Camera Modules": { en: "Camera Modules", zh: "相機模塊" },
  "Safety Enclosures": { en: "Safety Enclosures", zh: "安全圍欄" },
  "Midjourney / DALL-E": { en: "Midjourney / DALL-E", zh: "Midjourney / DALL-E" },
  "ChatGPT / Claude": { en: "ChatGPT / Claude", zh: "ChatGPT / Claude" },
  "AI Music Generators": { en: "AI Music Generators", zh: "AI 音樂生成器" },
  "Digital Art Tools": { en: "Digital Art Tools", zh: "數字藝術工具" },
  "Tablets or Chromebooks": { en: "Tablets or Chromebooks", zh: "平板電腦或 Chromebook" },
  "Drawing Tablets (Optional)": { en: "Drawing Tablets (Optional)", zh: "繪圖板 (可選)" },
  "Text-to-3D AI": { en: "Text-to-3D AI", zh: "文本到 3D AI" },
  "Tinkercad / Fusion 360": { en: "Tinkercad / Fusion 360", zh: "Tinkercad / Fusion 360" },
  "Arduino / Micro:bit IDE": { en: "Arduino / Micro:bit IDE", zh: "Arduino / Micro:bit IDE" },
  "3D Printers": { en: "3D Printers", zh: "3D 打印機" },
  "Micro:bit or Arduino Kits": { en: "Micro:bit or Arduino Kits", zh: "Micro:bit 或 Arduino 套件" },
  "Various Sensors (Temp, Light, Motion)": { en: "Various Sensors (Temp, Light, Motion)", zh: "各種傳感器 (溫度、光線、運動)" },
  "No-Code App Builders": { en: "No-Code App Builders", zh: "無代碼應用構建器" },
  "AI Market Research Tools": { en: "AI Market Research Tools", zh: "AI 市場研究工具" },
  "Presentation Software": { en: "Presentation Software", zh: "演示軟件" },
  "Laptops or Tablets": { en: "Laptops or Tablets", zh: "筆記本電腦或平板電腦" },
  "AR Development Platforms (e.g., Spark AR)": { en: "AR Development Platforms (e.g., Spark AR)", zh: "AR 開發平台 (例如 Spark AR)" },
  "Photogrammetry Software": { en: "Photogrammetry Software", zh: "攝影測量軟件" },
  "Audio/Video Editing": { en: "Audio/Video Editing", zh: "音頻/視頻編輯" },
  "Smartphones/Tablets with AR capabilities": { en: "Smartphones/Tablets with AR capabilities", zh: "具備 AR 功能的智能手機/平板電腦" },
  "360 Cameras (Optional)": { en: "360 Cameras (Optional)", zh: "360 相機 (可選)" },
  "Audio Recording Gear": { en: "Audio Recording Gear", zh: "錄音設備" },

  // Module Skills
  "Aerospace Engineering": { en: "Aerospace Engineering", zh: "航空航天工程" },
  "Data Science / Big Data": { en: "Data Science / Big Data", zh: "數據科學 / 大數據" },
  "AI Computer Vision": { en: "AI Computer Vision", zh: "AI 計算機視覺" },
  "Biology Research (\"Space Seeds\")": { en: "Biology Research (\"Space Seeds\")", zh: "生物學研究（「太空種子」）" },
  "Drone Technology (UAV)": { en: "Drone Technology (UAV)", zh: "無人機技術 (UAV)" },
  "IoT (Internet of Things)": { en: "IoT (Internet of Things)", zh: "物聯網 (IoT)" },
  "Sensor Technology (Spectrometers)": { en: "Sensor Technology (Spectrometers)", zh: "傳感器技術 (光譜儀)" },
  "Environmental Data Analysis": { en: "Environmental Data Analysis", zh: "環境數據分析" },
  "Hardware Coding & Assembly": { en: "Hardware Coding & Assembly", zh: "硬件編程與組裝" },
  "Prompt Engineering": { en: "Prompt Engineering", zh: "提示工程" },
  "Artificial Intelligence": { en: "Artificial Intelligence", zh: "人工智能" },
  "Natural Language Programming": { en: "Natural Language Programming", zh: "自然語言編程" },
  "Web Application Development": { en: "Web Application Development", zh: "Web 應用程序開發" },
  "UI/UX Logic": { en: "UI/UX Logic", zh: "UI/UX 邏輯" },
  "AI-to-CAD Generation": { en: "AI-to-CAD Generation", zh: "AI 到 CAD 生成" },
  "3D Printing (FDM/SLA)": { en: "3D Printing (FDM/SLA)", zh: "3D 打印 (FDM/SLA)" },
  "Advanced Manufacturing / Maker Ed": { en: "Advanced Manufacturing / Maker Ed", zh: "先進製造 / 創客教育" },
  "IoT Sensor Integration": { en: "IoT Sensor Integration", zh: "物聯網傳感器集成" },
  "Edge Computing": { en: "Edge Computing", zh: "邊緣計算" },
  "Generative AI (Visuals & Branding)": { en: "Generative AI (Visuals & Branding)", zh: "生成式 AI (視覺與品牌)" },
  "ArtTech": { en: "ArtTech", zh: "藝術科技" },
  "3D Modeling (Blind-box toys)": { en: "3D Modeling (Blind-box toys)", zh: "3D 建模 (盲盒玩具)" },
  "Tech Entrepreneurship": { en: "Tech Entrepreneurship", zh: "科技創業" },
  "Business Strategy (BOM, Market Validation, Pitching)": { en: "Business Strategy (BOM, Market Validation, Pitching)", zh: "商業戰略 (BOM、市場驗證、推介)" },
  "AI Video Generation": { en: "AI Video Generation", zh: "AI 視頻生成" },
  "Voice Cloning": { en: "Voice Cloning", zh: "語音克隆" },
  "Digital Humans / Virtual Avatars": { en: "Digital Humans / Virtual Avatars", zh: "數字人 / 虛擬化身" },
  "Natural Language Processing (NLP)": { en: "Natural Language Processing (NLP)", zh: "自然語言處理 (NLP)" },
  "Digital Media Production": { en: "Digital Media Production", zh: "數字媒體製作" },

  // Module Descriptions
  "Space education reimagined. AI adapts content instantly. Dive deep into data from telescopes and missions.": { en: "Space education reimagined. AI adapts content instantly. Dive deep into data from telescopes and missions.", zh: "重新構想的太空教育。AI 即時調整內容。深入研究來自望遠鏡和任務的數據。" },
  "Drone education elevated. AI analyzes flight paths and data. Master the low-altitude economy.": { en: "Drone education elevated. AI analyzes flight paths and data. Master the low-altitude economy.", zh: "無人機教育升級。AI 分析飛行路徑和數據。掌握低空經濟。" },
  "Master the art of prompting. Generate code, art, and music. Become a Vibe Coding expert.": { en: "Master the art of prompting. Generate code, art, and music. Become a Vibe Coding expert.", zh: "掌握提示的藝術。生成代碼、藝術和音樂。成為 Vibe 編程專家。" },
  "From text to physical objects. AI generates 3D models. Print and assemble smart devices.": { en: "From text to physical objects. AI generates 3D models. Print and assemble smart devices.", zh: "從文本到實物。AI 生成 3D 模型。打印並組裝智能設備。" },
  "Launch your tech startup. AI assists in market research and branding. Pitch to investors.": { en: "Launch your tech startup. AI assists in market research and branding. Pitch to investors.", zh: "啟動您的科技初創公司。AI 協助市場研究和品牌推廣。向投資者推介。" },
  "Preserve history with AI. Create digital humans and interactive stories. Connect generations.": { en: "Preserve history with AI. Create digital humans and interactive stories. Connect generations.", zh: "用 AI 保存歷史。創建數字人和互動故事。連接世代。" },

  "Low-Altitude Economy (Drones)": { en: "Low-Altitude Economy (Drones)", zh: "低空經濟 (無人機)" },
  "Build and code IoT-enabled drones equipped with spectrometers to capture real-world climate data.": { en: "Build and code IoT-enabled drones equipped with spectrometers to capture real-world climate data.", zh: "構建和編寫配備光譜儀的物聯網無人機，以捕獲真實世界的氣候數據。" },
  "AI Prompt Lab (Vibe Coding)": { en: "AI Prompt Lab (Vibe Coding)", zh: "AI 提示實驗室 (Vibe 編程)" },
  "Prompting is the new coding. Build and ship functional web apps in plain English using modern AI stacks.": { en: "Prompting is the new coding. Build and ship functional web apps in plain English using modern AI stacks.", zh: "提示是新的編程。使用現代 AI 堆棧用純英語構建和發布功能齊全的 Web 應用程序。" },
  "Smart City IoT Forge (Maker Ed)": { en: "Smart City IoT Forge (Maker Ed)", zh: "智慧城市物聯網鍛造 (創客教育)" },
  "“One sentence turns into a 3D model.” Master AI-to-CAD generation, 3D printing, and IoT sensor integration.": { en: "“One sentence turns into a 3D model.” Master AI-to-CAD generation, 3D printing, and IoT sensor integration.", zh: "「一句話變成 3D 模型。」掌握 AI 到 CAD 生成、3D 打印和物聯網傳感器集成。" },
  "ArtTech & Young CEO": { en: "ArtTech & Young CEO", zh: "藝術科技與青年 CEO" },
  "The ultimate startup bootcamp. Use Gen-AI for branding, design 3D blind-box toys, and pitch to investors.": { en: "The ultimate startup bootcamp. Use Gen-AI for branding, design 3D blind-box toys, and pitch to investors.", zh: "終極創業訓練營。使用生成式 AI 進行品牌推廣，設計 3D 盲盒玩具，並向投資者推介。" },
  "AI Movie & Director Program": { en: "AI Movie & Director Program", zh: "AI 電影與導演計劃" },
  "Step into a Hollywood-grade virtual studio! Master AI video generation, voice cloning, and digital human creation to produce impactful films.": { en: "Step into a Hollywood-grade virtual studio! Master AI video generation, voice cloning, and digital human creation to produce impactful films.", zh: "踏入好萊塢級別的虛擬工作室！掌握 AI 視頻生成、語音克隆和數字人創建，製作具有影響力的電影。" },

  "EXPLORE MORE": { en: "EXPLORE MORE", zh: "探索更多" },
  "MODULE": { en: "MODULE", zh: "模組" },

  "Partner With Us": { en: "Partner With Us", zh: "與我們合作" },
};

export default function App() {
  const [lang, setLang] = useState<'en' | 'zh'>('en');
  const t = (key: string) => translations[key]?.[lang] || key;

  const [activeSection, setActiveSection] = useState('hero');

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { threshold: 0.3 }
    );

    const sectionIds = ['hero', 'philosophy', 'for-schools', 'for-families', 'for-ngos', 'qef'];
    sectionIds.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const [selectedModuleId, setSelectedModuleId] = useState<string | null>(null);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [contactModalTitle, setContactModalTitle] = useState("");
  const [contactCategory, setContactCategory] = useState("");
  const [contactModule, setContactModule] = useState("");
  const [contactTheme, setContactTheme] = useState<'blue'|'purple'|'emerald'|'orange'|'rose'|'cyan'|'default'>('default');
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);
  const [isPrivacyModalOpen, setIsPrivacyModalOpen] = useState(false);
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);

  const openContactModal = (title: string, category: string = "", moduleName: string = "", theme: 'blue'|'purple'|'emerald'|'orange'|'rose'|'cyan'|'default' = 'default') => {
    setContactModalTitle(title);
    setContactCategory(category);
    setContactModule(moduleName);
    setContactTheme(theme);
    setIsContactModalOpen(true);
  };

  console.log('App component rendering...');

  useEffect(() => {
    if (selectedModuleId) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [selectedModuleId]);

  const selectedModule = selectedModuleId ? modulesData.find(m => m.id === selectedModuleId) : null;

  return (
    <I18nContext.Provider value={{ t }}>
      <ContactModal 
        isOpen={isContactModalOpen} 
        onClose={() => setIsContactModalOpen(false)} 
        title={contactModalTitle} 
        category={contactCategory}
        moduleName={contactModule}
        themeColor={contactTheme}
        t={t}
      />
      <AnimatePresence>
        {selectedModule && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 overflow-y-auto bg-black"
          >
            <ModuleDetail 
              module={selectedModule} 
              onBack={() => setSelectedModuleId(null)} 
              onBook={() => openContactModal(t("Book This Module"), t("For Schools"), selectedModule.title, "blue")}
              t={t}
            />
          </motion.div>
        )}
      </AnimatePresence>

      <main className="relative min-h-screen w-full flex flex-col bg-black text-white font-sans overflow-x-hidden">
      {/* Global Background Effects */}
      <div className="fixed inset-0 bg-grid pointer-events-none opacity-20" />
      <SpaceshipBackground />
      
      {/* Floating Orbs for Innovation Sections */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
        <motion.div 
          animate={{
            x: [0, 100, -50, 0],
            y: [0, -100, 50, 0],
            scale: [1, 1.2, 0.9, 1],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className="absolute top-[20%] left-[10%] w-[40vw] h-[40vw] rounded-full bg-white/[0.02] blur-[120px]"
        />
        <motion.div 
          animate={{
            x: [0, -150, 100, 0],
            y: [0, 150, -100, 0],
            scale: [1, 0.8, 1.1, 1],
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className="absolute bottom-[20%] right-[10%] w-[50vw] h-[50vw] rounded-full bg-white/[0.02] blur-[150px]"
        />
      </div>

      {/* Dynamic Background Glow */}
      <motion.div
        className="fixed inset-0 pointer-events-none z-0 transition-colors duration-1000"
        style={{
          background: activeSection === 'for-schools' ? 'radial-gradient(circle at 50% 50%, rgba(59,130,246,0.05) 0%, transparent 70%)' :
                      activeSection === 'for-families' ? 'radial-gradient(circle at 50% 50%, rgba(168,85,247,0.05) 0%, transparent 70%)' :
                      activeSection === 'for-ngos' ? 'radial-gradient(circle at 50% 50%, rgba(16,185,129,0.05) 0%, transparent 70%)' :
                      'transparent'
        }}
      />

      {/* Scroll Spy Side Nav */}
      <div className="fixed right-6 top-1/2 -translate-y-1/2 z-[100] hidden lg:flex flex-col gap-4">
        {['hero', 'philosophy', 'for-schools', 'for-families', 'for-ngos', 'qef'].map((id) => (
          <button
            key={id}
            onClick={() => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' })}
            className={`w-3 h-3 rounded-full transition-all duration-300 ${activeSection === id ? 'bg-white scale-150 shadow-[0_0_10px_rgba(255,255,255,0.8)]' : 'bg-white/20 hover:bg-white/50'}`}
            aria-label={`Scroll to ${id}`}
          />
        ))}
      </div>

      {/* Background Video */}
      <section id="hero" className="relative h-screen w-full flex flex-col shrink-0 overflow-hidden">
        <video
          className="absolute top-0 left-0 w-full h-full object-cover"
          src="https://d8j0ntlcm91z4.cloudfront.net/user_38xzZboKViGWJOttwIXH07lWA1P/hf_20260217_030345_246c0224-10a4-422c-b324-070b7c0eceda.mp4"
          autoPlay
          muted
          loop
          playsInline
        />
        <div className="absolute top-0 left-0 w-full h-full bg-black/60" />

        {/* Navigation */}
        <nav className="relative z-50 flex items-center justify-between px-6 md:px-[60px] lg:px-[120px] py-6 md:py-[20px] w-full">
          {/* Left: Menu - Removed as requested */}
          <div className="hidden md:flex flex-1" />

          {/* Center: Logo */}
          <div className="flex items-center md:absolute md:left-1/2 md:-translate-x-1/2">
            <img 
              src="logo.png" 
              alt="CreaTech Innovation Academy" 
              className="h-20 md:h-40 w-auto object-contain"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
              }}
            />
          </div>

          {/* Right: Button */}
          <div className="flex justify-end flex-1 items-center gap-4">
            <button 
              onClick={() => setLang(lang === 'en' ? 'zh' : 'en')}
              className="text-white/70 hover:text-white text-sm font-bold uppercase tracking-widest transition-colors"
            >
              {lang === 'en' ? '繁體中文' : 'EN'}
            </button>
            <button onClick={() => openContactModal(t("Book Consultation"), "General Inquiry", "General", "blue")} className="relative group">
              <div className="absolute inset-0 rounded-full border-[0.6px] border-white group-hover:border-white/50 transition-colors" />
              <div className="relative bg-black rounded-full px-5 md:px-[29px] py-2.5 md:py-[11px] text-[13px] md:text-[16px] font-bold uppercase tracking-widest overflow-hidden">
                <div className="button-glow" />
                <span className="hidden sm:inline">{t("Book Consultation")}</span>
                <span className="sm:hidden">{t("Book")}</span>
              </div>
            </button>
          </div>
        </nav>

        {/* Hero Content */}
        <div className="relative z-10 flex flex-col items-center text-center px-6 pt-[120px] md:pt-[240px] pb-[80px] md:pb-[102px] flex-grow">
          <motion.div 
            initial="hidden"
            animate="visible"
            variants={{
              hidden: { opacity: 0, y: 20 },
              visible: {
                opacity: 1,
                y: 0,
                transition: {
                  duration: 0.8,
                  staggerChildren: 0.1
                }
              }
            }}
            className="flex flex-col items-center gap-6 md:gap-[40px]"
          >
            {/* Headline */}
            <h1 className="text-[36px] sm:text-[48px] md:text-[96px] font-medium leading-[1.1] max-w-[900px] text-silver">
              {t("DESIGN. BUILD.")}<br />
              {t("SHIP THE FUTURE.")}
            </h1>

            {/* Subtext */}
            <p className="text-[18px] md:text-[20px] font-light text-white/90 max-w-[680px] leading-relaxed md:mt-[-16px]">
              {t("Hong Kong’s premier ArtTech partner providing turnkey EdTech curricula.")}
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row flex-wrap justify-center gap-4 md:gap-6 mt-6 w-full sm:w-auto">
              <motion.button 
                variants={{
                  hidden: { opacity: 0, y: 10 },
                  visible: { opacity: 1, y: 0 }
                }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => document.getElementById('for-schools')?.scrollIntoView({ behavior: 'smooth' })}
                className="relative group w-full sm:w-auto"
              >
                <div className="absolute inset-0 rounded-full border-[1px] border-blue-500/50 group-hover:border-blue-400 transition-colors" />
                <div className="relative bg-blue-600/20 backdrop-blur-md text-blue-50 rounded-full px-8 py-3.5 text-[16px] md:text-[18px] font-semibold overflow-hidden shadow-[0_0_15px_rgba(59,130,246,0.3)] group-hover:shadow-[0_0_25px_rgba(59,130,246,0.5)] transition-shadow flex items-center justify-center gap-3">
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-600/0 via-blue-400/10 to-blue-600/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />
                  <BookOpen className="w-5 h-5" />
                  {t("For Schools")}
                </div>
              </motion.button>
              <motion.button 
                variants={{
                  hidden: { opacity: 0, y: 10 },
                  visible: { opacity: 1, y: 0 }
                }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => document.getElementById('for-families')?.scrollIntoView({ behavior: 'smooth' })}
                className="relative group w-full sm:w-auto"
              >
                <div className="absolute inset-0 rounded-full border-[1px] border-purple-500/50 group-hover:border-purple-400 transition-colors" />
                <div className="relative bg-purple-600/20 backdrop-blur-md text-purple-50 rounded-full px-8 py-3.5 text-[16px] md:text-[18px] font-semibold overflow-hidden shadow-[0_0_15px_rgba(168,85,247,0.3)] group-hover:shadow-[0_0_25px_rgba(168,85,247,0.5)] transition-shadow flex items-center justify-center gap-3">
                  <div className="absolute inset-0 bg-gradient-to-r from-purple-600/0 via-purple-400/10 to-purple-600/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />
                  <Users className="w-5 h-5" />
                  {t("For Families and Individuals")}
                </div>
              </motion.button>
              <motion.button 
                variants={{
                  hidden: { opacity: 0, y: 10 },
                  visible: { opacity: 1, y: 0 }
                }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => document.getElementById('for-ngos')?.scrollIntoView({ behavior: 'smooth' })}
                className="relative group w-full sm:w-auto"
              >
                <div className="absolute inset-0 rounded-full border-[1px] border-emerald-500/50 group-hover:border-emerald-400 transition-colors" />
                <div className="relative bg-emerald-600/20 backdrop-blur-md text-emerald-50 rounded-full px-8 py-3.5 text-[16px] md:text-[18px] font-semibold overflow-hidden shadow-[0_0_15px_rgba(16,185,129,0.3)] group-hover:shadow-[0_0_25px_rgba(16,185,129,0.5)] transition-shadow flex items-center justify-center gap-3">
                  <div className="absolute inset-0 bg-gradient-to-r from-emerald-600/0 via-emerald-400/10 to-emerald-600/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />
                  <Globe className="w-5 h-5" />
                  {t("For NGOs and Associations")}
                </div>
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>      {/* Combined Philosophy & Innovation Section */}
      <section id="philosophy" className="relative z-10 py-24 md:py-48 bg-black overflow-hidden border-y border-white/5">
        {/* Background Grid Effect */}
        <div className="absolute inset-0 opacity-[0.05] pointer-events-none" 
             style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '60px 60px' }} />
        
        {/* Animated Background Elements */}
        <motion.div 
          animate={{ 
            scale: [1, 1.2, 1],
            opacity: [0.1, 0.2, 0.1],
            rotate: [0, 90, 0]
          }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
          className="absolute top-1/4 -left-20 w-96 h-96 bg-blue-500/10 rounded-full blur-[120px] pointer-events-none" 
        />
        <motion.div 
          animate={{ 
            scale: [1.2, 1, 1.2],
            opacity: [0.1, 0.15, 0.1],
            rotate: [0, -90, 0]
          }}
          transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
          className="absolute bottom-1/4 -right-20 w-96 h-96 bg-purple-500/10 rounded-full blur-[120px] pointer-events-none" 
        />

        <div className="max-w-[1400px] mx-auto px-6 md:px-[60px] lg:px-[120px] relative">
          {/* Part 1: Philosophy Pillars */}
          <div className="grid grid-cols-1 md:grid-cols-3 border border-white/10 mb-32 md:mb-64 overflow-hidden rounded-2xl bg-white/[0.01] backdrop-blur-sm">
            
            {/* Vision & Mission */}
            <motion.div 
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="flex flex-col gap-8 p-10 md:p-12 relative border-b md:border-b-0 md:border-r border-white/10 group hover:bg-white/[0.02] transition-colors duration-500"
            >
              {/* Large Background Number */}
              <div className="absolute top-8 right-8 text-[120px] font-bold text-white/[0.02] leading-none select-none pointer-events-none group-hover:text-blue-500/[0.05] transition-colors duration-700">
                01
              </div>

              <div className="flex flex-col gap-6 relative z-10">
                <div className="flex items-center gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-500 shadow-[0_0_10px_rgba(59,130,246,0.5)]" />
                  <span className="text-[14px] uppercase tracking-[0.5em] text-blue-400/80 font-bold">{t("Strategic Vision")}</span>
                </div>
                
                <h3 className="text-[28px] md:text-[32px] font-medium leading-[1.2] text-white group-hover:text-blue-400 transition-colors duration-500">
                  {t("\"We transform students from passive consumers into multi-domain tech creators.\"")}
                </h3>

                <div className="h-px w-full bg-gradient-to-r from-blue-500/30 to-transparent" />

                <p className="text-[18px] text-white/50 leading-relaxed font-light group-hover:text-white/70 transition-colors duration-500">
                  {t("Empowering the next generation to not just use technology, but to architect the future through creative mastery.")}
                </p>

                <div className="mt-4 flex items-center gap-4">
                  <div className="text-[13px] font-mono text-blue-500/60">{t("STATUS: ACTIVE")}</div>
                  <div className="flex-1 h-[1px] bg-blue-500/20" />
                </div>
              </div>
            </motion.div>

            {/* Productive Failure */}
            <motion.div 
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="flex flex-col gap-8 p-10 md:p-12 relative border-b md:border-b-0 md:border-r border-white/10 group hover:bg-white/[0.02] transition-colors duration-500"
            >
              {/* Large Background Number */}
              <div className="absolute top-8 right-8 text-[120px] font-bold text-white/[0.02] leading-none select-none pointer-events-none group-hover:text-purple-500/[0.05] transition-colors duration-700">
                02
              </div>

              <div className="flex flex-col gap-6 relative z-10">
                <div className="flex items-center gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-purple-500 shadow-[0_0_10px_rgba(168,85,247,0.5)]" />
                  <span className="text-[14px] uppercase tracking-[0.5em] text-purple-400/80 font-bold">{t("Pedagogical Core")}</span>
                </div>
                
                <h3 className="text-[28px] md:text-[32px] font-medium leading-[1.2] text-white group-hover:text-purple-400 transition-colors duration-500">
                  {t("Productive Failure as a Catalyst for Innovation.")}
                </h3>

                <div className="h-px w-full bg-gradient-to-r from-purple-500/30 to-transparent" />

                <p className="text-[18px] text-white/50 leading-relaxed font-light group-hover:text-white/70 transition-colors duration-500">
                  {t("Our pedagogy focuses on learning through experimentation, iteration, and high-stakes problem solving. We believe that true mastery comes from navigating the space between what you know and what you're building.")}
                </p>

                <div className="mt-4 flex items-center gap-4">
                  <div className="text-[13px] font-mono text-purple-500/60">{t("METHOD: ITERATIVE")}</div>
                  <div className="flex-1 h-[1px] bg-purple-500/20" />
                </div>
              </div>
            </motion.div>

            {/* Academic Authority */}
            <motion.div 
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="flex flex-col gap-8 p-10 md:p-12 relative group hover:bg-white/[0.02] transition-colors duration-500"
            >
              {/* Large Background Number */}
              <div className="absolute top-8 right-8 text-[120px] font-bold text-white/[0.02] leading-none select-none pointer-events-none group-hover:text-emerald-500/[0.05] transition-colors duration-700">
                03
              </div>

              <div className="flex flex-col gap-6 relative z-10">
                <div className="flex items-center gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 shadow-[0_0_10px_rgba(16,185,129,0.5)]" />
                  <span className="text-[14px] uppercase tracking-[0.5em] text-emerald-400/80 font-bold">{t("Academic Trust")}</span>
                </div>
                
                <h3 className="text-[28px] md:text-[32px] font-medium leading-[1.2] text-white group-hover:text-emerald-400 transition-colors duration-500">
                  {t("Rigorous Standards Guided by Expert Authority.")}
                </h3>

                <div className="h-px w-full bg-gradient-to-r from-emerald-500/30 to-transparent" />

                <p className="text-[18px] text-white/50 leading-relaxed font-light group-hover:text-white/70 transition-colors duration-500">
                  {t("Trusted by schools, associations, and NGOs, and guided by an advisory board of university professors. Our curriculum is built on rigorous academic standards and real-world industry practices.")}
                </p>

                <div className="mt-4 flex items-center gap-4">
                  <div className="flex -space-x-1.5">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="w-5 h-5 rounded-full bg-emerald-500/20 border border-black flex items-center justify-center">
                        <ShieldCheck className="w-2.5 h-2.5 text-emerald-400" />
                      </div>
                    ))}
                  </div>
                  <div className="text-[13px] font-mono text-emerald-500/60 uppercase">{t("VERIFIED: QEF")}</div>
                  <div className="flex-1 h-[1px] bg-emerald-500/20" />
                </div>
              </div>
            </motion.div>
          </div>

          {/* Part 2: Innovation Phases */}
          <div className="flex flex-col gap-[120px] md:gap-[180px]">
            
            {/* Section 1: Empower Schools */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-24 items-center">
              <div className="flex flex-col gap-6">
                <div className="flex items-center gap-3">
                  <div className="w-1 h-6 bg-white" />
                  <span className="text-[16px] font-bold uppercase tracking-widest text-white/50">{t("Phase 01")}</span>
                </div>
                <div className="box">
                  <h2 className="text-[32px] md:text-[48px] font-medium leading-tight text-blue-400">
                    {t("Empower Schools")}
                  </h2>
                </div>
                <p className="text-[20px] text-white leading-relaxed max-w-[540px]">
                  {t("We provide turnkey EdTech curricula, hardware, and expert support to help schools secure government grants ranging from ")}<span className="text-white font-bold">{t("HKD 500k to 2M")}</span>{t(". Our mission is to modernize the classroom experience with seamless integration.")}
                </p>
              </div>
              <div className="aspect-[4/5] md:aspect-[16/10] overflow-hidden rounded-[4px] border border-white/10">
                <img 
                  src="https://images.unsplash.com/photo-1509062522246-3755977927d7?auto=format&fit=crop&q=80&w=1200" 
                  alt="Empower Schools"
                  className="w-full h-full object-cover opacity-80 hover:opacity-100 transition-opacity duration-700"
                  referrerPolicy="no-referrer"
                />
              </div>
            </div>

            {/* Section 2: Prepare Students */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-24 items-center">
              <div className="aspect-[4/5] md:aspect-[16/10] overflow-hidden rounded-[4px] border border-white/10 order-2 md:order-1">
                <img 
                  src="https://images.unsplash.com/photo-1523240795612-9a054b0db644?auto=format&fit=crop&q=80&w=1200" 
                  alt="Prepare Students"
                  className="w-full h-full object-cover opacity-80 hover:opacity-100 transition-opacity duration-700"
                  referrerPolicy="no-referrer"
                />
              </div>
              <div className="flex flex-col gap-6 order-1 md:order-2">
                <div className="flex items-center gap-3">
                  <div className="w-1 h-6 bg-white" />
                  <span className="text-[16px] font-bold uppercase tracking-widest text-white/50">{t("Phase 02")}</span>
                </div>
                <div className="box">
                  <h2 className="text-[32px] md:text-[48px] font-medium leading-tight text-purple-400">
                    {t("Prepare Students")}
                  </h2>
                </div>
                <p className="text-[20px] text-white leading-relaxed max-w-[540px]">
                  {t("We help students build elite ")}<span className="text-white font-bold">{t("STEAM portfolios")}</span>{t(" featuring real-world prototypes. Our mentorship is designed to significantly enhance university admissions for competitive pathways like ")}<span className="text-white font-bold">{t("JUPAS")}</span>{t(" and the ")}<span className="text-white font-bold">{t("Ivy League")}</span>{t(".")}
                </p>
              </div>
            </div>

            {/* Section 3: Drive Social Impact */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-24 items-center">
              <div className="flex flex-col gap-6">
                <div className="flex items-center gap-3">
                  <div className="w-1 h-6 bg-white" />
                  <span className="text-[16px] font-bold uppercase tracking-widest text-white/50">{t("Phase 03")}</span>
                </div>
                <div className="box">
                  <h2 className="text-[32px] md:text-[48px] font-medium leading-tight text-emerald-400">
                    {t("Drive Social Impact")}
                  </h2>
                </div>
                <p className="text-[20px] text-white leading-relaxed max-w-[540px]">
                  {t("We bridge the digital divide through social inclusion projects. From training seniors in AI to masterminding ")}<span className="text-white font-bold">{t("ESG-aligned sustainability initiatives")}</span>{t(", we ensure technology serves the entire community.")}
                </p>
              </div>
              <div className="aspect-[4/5] md:aspect-[16/10] overflow-hidden rounded-[4px] border border-white/10">
                <img 
                  src="https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&q=80&w=1200" 
                  alt="Social Impact"
                  className="w-full h-full object-cover opacity-80 hover:opacity-100 transition-opacity duration-700"
                  referrerPolicy="no-referrer"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Elite STEAM Curricula Section */}
      <section id="for-schools" className="relative z-10 bg-black py-16 md:py-[80px] px-6 md:px-[60px] lg:px-[120px] overflow-hidden">
        {/* Section Glows */}
        <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-blue-500/50 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-indigo-500/30 to-transparent" />
        
        {/* Animated Background Elements */}
        <motion.div 
          animate={{ opacity: [0.05, 0.15, 0.05] }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
          className="absolute inset-0 bg-grid pointer-events-none" 
        />
        
        {/* Moving Gradient Orbs */}
        <motion.div 
          animate={{ 
            x: [0, 150, 0],
            y: [0, -100, 0],
            scale: [1, 1.4, 1],
            opacity: [0.05, 0.1, 0.05]
          }}
          transition={{ 
            duration: 15, 
            repeat: Infinity, 
            ease: "easeInOut" 
          }}
          className="absolute top-0 right-0 w-[1000px] h-[1000px] bg-blue-500/20 blur-[180px] rounded-full -translate-y-1/2 translate-x-1/4 pointer-events-none" 
        />
        <motion.div 
          animate={{ 
            x: [0, -120, 0],
            y: [0, 100, 0],
            scale: [1, 1.3, 1],
            opacity: [0.03, 0.08, 0.03]
          }}
          transition={{ 
            duration: 20, 
            repeat: Infinity, 
            ease: "easeInOut",
            delay: 2
          }}
          className="absolute bottom-0 left-0 w-[800px] h-[800px] bg-indigo-500/15 blur-[160px] rounded-full translate-y-1/2 -translate-x-1/4 pointer-events-none" 
        />
        
        {/* Floating Data Particles */}
        <div className="absolute inset-0 pointer-events-none">
          {[...Array(15)].map((_, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0 }}
              animate={{ 
                opacity: [0.1, 0.4, 0.1],
                y: [0, -150, 0],
                x: [0, Math.sin(i) * 80, 0],
                rotate: [0, 180, 360]
              }}
              transition={{ 
                duration: 10 + i * 2, 
                repeat: Infinity, 
                ease: "linear",
                delay: i * 0.5
              }}
              className="absolute text-[11px] md:text-[13px] font-mono text-blue-400/40 whitespace-nowrap select-none drop-shadow-[0_0_8px_rgba(96,165,250,0.3)]"
              style={{
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
              }}
            >
              {`0x${Math.random().toString(16).slice(2, 8).toUpperCase()}_STREAM_${i}`}
            </motion.div>
          ))}
        </div>
        
        <div className="max-w-[1400px] mx-auto relative z-10">
          {/* Header Area: Editorial Style */}
          <div className="flex flex-col gap-12 mb-[60px]">
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-4">
                <motion.div 
                  initial={{ opacity: 0, scale: 0.8, y: 20 }}
                  whileInView={{ opacity: 1, scale: 1, y: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                  className="px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/20"
                >
                  <span className="text-[12px] font-bold uppercase tracking-[0.2em] text-blue-400">{t("For Schools")}</span>
                </motion.div>
                <motion.span 
                  initial={{ opacity: 0, x: -40, filter: "blur(10px)" }}
                  whileInView={{ opacity: 1, x: 0, filter: "blur(0px)" }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                  className="text-[14px] font-bold uppercase tracking-[0.4em] text-blue-400/60"
                >
                  {t("Elite STEAM Curricula")}
                </motion.span>
              </div>
              <div className="box">
                <motion.h2 
                  initial={{ opacity: 0, y: 100, rotateX: 45 }}
                  whileInView={{ opacity: 1, y: 0, rotateX: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
                  className="text-[32px] sm:text-[42px] md:text-[64px] font-medium leading-[0.9] tracking-tighter uppercase text-blue-400"
                >
                  {t("Mastering")} <br />
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-blue-600">{t("The Future")}</span>
                </motion.h2>
              </div>
            </div>
            
            <div className="flex flex-col md:flex-row justify-between items-end gap-8">
              <p className="text-[18px] md:text-[20px] text-white/40 font-light max-w-[600px] leading-relaxed italic">
                {t("\"We don't just teach technology; we build the architects of tomorrow's digital landscape.\"")}
                <br />
                <span className="text-[14px] mt-4 block not-italic font-medium text-blue-500/60 uppercase tracking-widest">{t("Available for QEF School Licensing & 12-Week Bootcamps")}</span>
              </p>
              
              <motion.button 
                whileHover={{ scale: 1.1, y: -5 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => openContactModal(t("License Now"), t("For Schools"), t("Elite STEAM Curricula"), "blue")}
                className="relative group overflow-hidden rounded-full"
              >
                <div className="absolute inset-0 bg-blue-600 translate-y-full group-hover:translate-y-0 transition-transform duration-500" />
                <div className="relative border border-white/20 px-10 py-5 rounded-full flex items-center gap-4 transition-colors group-hover:border-transparent group-hover:text-white">
                  <span className="text-[16px] font-bold uppercase tracking-widest">{t("License Now")}</span>
                  <ArrowUpRight className="w-5 h-5 group-hover:rotate-45 transition-transform duration-500" />
                </div>
              </motion.button>
            </div>
          </div>

          {/* Curricula Grid: Brutalist/Editorial Style with Image in Bottom Right */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border-silver">
            
            {/* Module 1: Space Education */}
            <HighTechCard 
              className="bg-titanium flex flex-col min-h-[520px] transition-all duration-500 relative border-silver" 
              spotlightColor="rgba(59,130,246,0.15)"
              skills={[t("Aerospace Engineering"), t("Data Science / Big Data"), t("AI Computer Vision"), t("Generative AI (Spatial Design)"), t("Biology Research (\"Space Seeds\")")]}
              onClick={() => setSelectedModuleId('space-education')}
            >
              <div className="aspect-[2/3] md:aspect-[3/2] overflow-hidden relative group-hover:scale-[1.02] transition-transform duration-700">
                <img 
                  src="https://images.unsplash.com/photo-1446776811953-b23d57bd21aa?auto=format&fit=crop&q=80&w=800" 
                  alt="Space Tech"
                  className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700"
                  referrerPolicy="no-referrer"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              </div>
              
              <div className="p-8 md:p-10 flex flex-col gap-6 h-full">
                <div className="text-[12px] font-bold uppercase tracking-[0.3em] text-white/40 group-hover:text-blue-400/60 transition-colors">{t("MODULE")} 01</div>
                <h3 className="text-[28px] md:text-[32px] font-bold leading-tight text-white tracking-tight group-hover:text-blue-400 transition-colors">
                  🌌 {t("Space Education Powered by AI and Data Science")}
                </h3>
                <p className="text-[16px] text-white/50 leading-relaxed font-medium group-hover:text-blue-400/80 transition-colors">
                  {t("Space education reimagined. AI adapts content instantly. Dive deep into data from telescopes and missions.")}
                </p>
                
                <motion.div 
                  whileHover={{ scale: 1.05, x: 5 }}
                  className="mt-auto flex items-center gap-2 text-[14px] font-bold uppercase tracking-widest text-white/60 group-hover:text-blue-400 transition-all cursor-pointer"
                >
                  {t("EXPLORE MORE")} <ArrowUpRight className="w-4 h-4" />
                </motion.div>
              </div>
            </HighTechCard>

            {/* Module 2: Low-Altitude Economy */}
            <HighTechCard 
              className="bg-titanium flex flex-col min-h-[520px] transition-all duration-500 relative border-silver" 
              spotlightColor="rgba(16,185,129,0.15)"
              skills={["Drone Technology (UAV)", "IoT (Internet of Things)", "Sensor Technology (Spectrometers)", "Environmental Data Analysis", "Hardware Coding & Assembly"]}
              onClick={() => setSelectedModuleId('drone-missions')}
            >
              <div className="aspect-[2/3] md:aspect-[3/2] overflow-hidden relative group-hover:scale-[1.02] transition-transform duration-700">
                <img 
                  src="https://images.unsplash.com/photo-1508614589041-895b88991e3e?auto=format&fit=crop&q=80&w=800" 
                  alt="Drones"
                  className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700"
                  referrerPolicy="no-referrer"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              </div>
              
              <div className="p-8 md:p-10 flex flex-col gap-6 h-full">
                <div className="text-[12px] font-bold uppercase tracking-[0.3em] text-white/40 group-hover:text-emerald-400/60 transition-colors">MODULE 02</div>
                <h3 className="text-[28px] md:text-[32px] font-bold leading-tight text-white tracking-tight group-hover:text-emerald-400 transition-colors">
                  🚁 Low-Altitude <br />
                  Economy (Drones)
                </h3>
                <p className="text-[16px] text-white/50 leading-relaxed font-medium group-hover:text-emerald-400/80 transition-colors">
                  Build and code IoT-enabled drones equipped with spectrometers to capture real-world climate data.
                </p>
                
                <motion.div 
                  whileHover={{ scale: 1.05, x: 5 }}
                  className="mt-auto flex items-center gap-2 text-[14px] font-bold uppercase tracking-widest text-white/60 group-hover:text-emerald-400 transition-all cursor-pointer"
                >
                  EXPLORE MORE <ArrowUpRight className="w-4 h-4" />
                </motion.div>
              </div>
            </HighTechCard>

            {/* Module 3: AI Prompt Lab */}
            <HighTechCard 
              className="bg-titanium flex flex-col min-h-[520px] transition-all duration-500 relative border-silver" 
              spotlightColor="rgba(168,85,247,0.15)"
              skills={["Prompt Engineering", "Artificial Intelligence", "Natural Language Programming", "Web Application Development", "UI/UX Logic"]}
              onClick={() => setSelectedModuleId('vibe-coding')}
            >
              <div className="aspect-[2/3] md:aspect-[3/2] overflow-hidden relative group-hover:scale-[1.02] transition-transform duration-700">
                <img 
                  src="https://images.unsplash.com/photo-1550751827-4bd374c3f58b?auto=format&fit=crop&q=80&w=800" 
                  alt="Vibe Coding"
                  className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700"
                  referrerPolicy="no-referrer"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              </div>
              
              <div className="p-8 md:p-10 flex flex-col gap-6 h-full">
                <div className="text-[12px] font-bold uppercase tracking-[0.3em] text-white/40 group-hover:text-purple-400/60 transition-colors">MODULE 03</div>
                <h3 className="text-[28px] md:text-[32px] font-bold leading-tight text-white tracking-tight group-hover:text-purple-400 transition-colors">
                  🧠 AI Prompt Lab <br />
                  (Vibe Coding)
                </h3>
                <p className="text-[16px] text-white/50 leading-relaxed font-medium group-hover:text-purple-400/80 transition-colors">
                  Prompting is the new coding. Build and ship functional web apps in plain English using modern AI stacks.
                </p>
                
                <motion.div 
                  whileHover={{ scale: 1.05, x: 5 }}
                  className="mt-auto flex items-center gap-2 text-[14px] font-bold uppercase tracking-widest text-white/60 group-hover:text-purple-400 transition-all cursor-pointer"
                >
                  EXPLORE MORE <ArrowUpRight className="w-4 h-4" />
                </motion.div>
              </div>
            </HighTechCard>

            {/* Module 4: Smart City IoT Forge */}
            <HighTechCard 
              className="bg-[#1A1A1A] flex flex-col min-h-[520px] transition-all duration-500 relative border-silver" 
              spotlightColor="rgba(251,146,60,0.15)"
              skills={["AI-to-CAD Generation", "3D Printing (FDM/SLA)", "Advanced Manufacturing / Maker Ed", "IoT Sensor Integration", "Edge Computing"]}
              onClick={() => setSelectedModuleId('iot-maker-lab')}
            >
              <div className="aspect-[2/3] md:aspect-[3/2] overflow-hidden relative group-hover:scale-[1.02] transition-transform duration-700">
                <img 
                  src="https://images.unsplash.com/photo-1449824913935-59a10b8d2000?auto=format&fit=crop&q=80&w=800" 
                  alt="Smart City"
                  className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700"
                  referrerPolicy="no-referrer"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              </div>
              
              <div className="p-8 md:p-10 flex flex-col gap-6 h-full">
                <div className="text-[12px] font-bold uppercase tracking-[0.3em] text-white/40 group-hover:text-orange-400/60 transition-colors">MODULE 04</div>
                <h3 className="text-[28px] md:text-[32px] font-bold leading-tight text-white tracking-tight group-hover:text-orange-400 transition-colors">
                  🛠️ Smart City <br />
                  IoT Forge (Maker Ed)
                </h3>
                <p className="text-[16px] text-white/50 leading-relaxed font-medium group-hover:text-orange-400/80 transition-colors">
                  “One sentence turns into a 3D model.” Master AI-to-CAD generation, 3D printing, and IoT sensor integration.
                </p>
                
                <motion.div 
                  whileHover={{ scale: 1.05, x: 5 }}
                  className="mt-auto flex items-center gap-2 text-[14px] font-bold uppercase tracking-widest text-white/60 group-hover:text-orange-400 transition-all cursor-pointer"
                >
                  EXPLORE MORE <ArrowUpRight className="w-4 h-4" />
                </motion.div>
              </div>
            </HighTechCard>

            {/* Module 5: ArtTech & Young CEO */}
            <HighTechCard 
              className="bg-[#1A1A1A] flex flex-col min-h-[520px] transition-all duration-500 relative border-silver" 
              spotlightColor="rgba(251,113,133,0.15)"
              skills={["Generative AI (Visuals & Branding)", "ArtTech", "3D Modeling (Blind-box toys)", "Tech Entrepreneurship", "Business Strategy (BOM, Market Validation, Pitching)"]}
              onClick={() => setSelectedModuleId('tech-entrepreneurship')}
            >
              <div className="aspect-[2/3] md:aspect-[3/2] overflow-hidden relative group-hover:scale-[1.02] transition-transform duration-700">
                <img 
                  src="https://images.unsplash.com/photo-1556761175-b413da4baf72?auto=format&fit=crop&q=80&w=800" 
                  alt="Young CEO"
                  className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700"
                  referrerPolicy="no-referrer"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              </div>
              
              <div className="p-8 md:p-10 flex flex-col gap-6 h-full">
                <div className="text-[12px] font-bold uppercase tracking-[0.3em] text-white/40 group-hover:text-rose-400/60 transition-colors">MODULE 05</div>
                <h3 className="text-[28px] md:text-[32px] font-bold leading-tight text-white tracking-tight group-hover:text-rose-400 transition-colors">
                  💼 {t("ArtTech & Young CEO (Artech and Design Thinking)")}
                </h3>
                <p className="text-[16px] text-white/50 leading-relaxed font-medium group-hover:text-rose-400/80 transition-colors">
                  The ultimate startup bootcamp. Use Gen-AI for branding, design 3D blind-box toys, and pitch to investors.
                </p>
                
                <motion.div 
                  whileHover={{ scale: 1.05, x: 5 }}
                  className="mt-auto flex items-center gap-2 text-[14px] font-bold uppercase tracking-widest text-white/60 group-hover:text-rose-400 transition-all cursor-pointer"
                >
                  EXPLORE MORE <ArrowUpRight className="w-4 h-4" />
                </motion.div>
              </div>
            </HighTechCard>

            {/* Module 6: AI Movie & Director Program */}
            <HighTechCard 
              className="bg-[#1A1A1A] flex flex-col min-h-[520px] transition-all duration-500 relative border-silver" 
              spotlightColor="rgba(34,211,238,0.15)"
              skills={["AI Video Generation", "Voice Cloning", "Digital Humans / Virtual Avatars", "Natural Language Processing (NLP)", "Digital Media Production"]}
              onClick={() => setSelectedModuleId('digital-heritage')}
            >
              <div className="aspect-[2/3] md:aspect-[3/2] overflow-hidden relative group-hover:scale-[1.02] transition-transform duration-700">
                <img 
                  src="https://images.unsplash.com/photo-1485846234645-a62644f84728?auto=format&fit=crop&q=80&w=800" 
                  alt="AI Movie"
                  className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700"
                  referrerPolicy="no-referrer"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              </div>
              
              <div className="p-8 md:p-10 flex flex-col gap-6 h-full">
                <div className="text-[12px] font-bold uppercase tracking-[0.3em] text-white/40 group-hover:text-cyan-400/60 transition-colors">MODULE 06</div>
                <h3 className="text-[28px] md:text-[32px] font-bold leading-tight text-white tracking-tight group-hover:text-cyan-400 transition-colors">
                  🎬 {t("AI Movie & Director Program (AI Art and AI-assisted creation)")}
                </h3>
                <p className="text-[16px] text-white/50 leading-relaxed font-medium group-hover:text-cyan-400/80 transition-colors">
                  Step into a Hollywood-grade virtual studio! Master AI video generation, voice cloning, and digital human creation to produce impactful films.
                </p>
                
                <motion.div 
                  whileHover={{ scale: 1.05, x: 5 }}
                  className="mt-auto flex items-center gap-2 text-[14px] font-bold uppercase tracking-widest text-white/60 group-hover:text-cyan-400 transition-all cursor-pointer"
                >
                  EXPLORE MORE <ArrowUpRight className="w-4 h-4" />
                </motion.div>
              </div>
            </HighTechCard>

          </div>

          {/* Journey Content (Combined) */}
          <div className="mt-20 md:mt-[120px] relative overflow-hidden py-10 md:py-20">
            {/* Rotating Innovation Core */}
            <div className="max-w-[1200px] mx-auto relative h-[500px] md:h-[800px] flex items-center justify-center">
              
              {/* Central Core */}
              <div className="absolute z-20 flex flex-col items-center gap-4 md:gap-6">
                <motion.div 
                  animate={{ 
                    scale: [1, 1.05, 1],
                    boxShadow: [
                      "0 0 40px rgba(59,130,246,0.2)",
                      "0 0 80px rgba(59,130,246,0.4)",
                      "0 0 40px rgba(59,130,246,0.2)"
                    ]
                  }}
                  transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                  className="w-24 h-24 md:w-40 md:h-40 rounded-full bg-blue-600 flex items-center justify-center relative overflow-hidden group cursor-pointer shadow-[0_0_50px_rgba(59,130,246,0.3)]"
                >
                  <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-20" />
                  <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent" />
                  <Cpu className="w-10 h-10 md:w-16 md:h-16 text-white relative z-10 group-hover:scale-110 transition-transform duration-500" />
                  {/* Pulse Effect */}
                  <div className="absolute inset-0 border-4 border-white/10 rounded-full animate-ping opacity-20" />
                </motion.div>
                <div className="text-center">
                  <span className="text-[11px] md:text-[14px] font-bold uppercase tracking-[0.3em] md:tracking-[0.5em] text-blue-400 mb-1 md:mb-2 block">{t("Core Protocol")}</span>
                  <h3 className="text-[18px] md:text-[24px] font-black uppercase tracking-[0.1em] md:tracking-[0.2em] text-white">{t("Implementation")}</h3>
                </div>
              </div>

              {/* Rotating Rings */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                {/* Outer Ring */}
                <motion.div 
                  animate={{ rotate: 360 }}
                  transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
                  className="absolute w-[280px] h-[280px] md:w-[600px] md:h-[600px] rounded-full border border-white/5"
                />
                {/* Middle Ring with Dashes */}
                <motion.div 
                  animate={{ rotate: -360 }}
                  transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
                  className="absolute w-[380px] h-[380px] md:w-[900px] md:h-[900px] rounded-full border border-dashed border-blue-500/10"
                />
                {/* Inner Glow Ring */}
                <motion.div 
                  animate={{ rotate: 360 }}
                  transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
                  className="absolute w-[180px] h-[180px] md:w-[400px] md:h-[400px] rounded-full border border-blue-500/30 shadow-[inset_0_0_50px_rgba(59,130,246,0.2)]"
                />
              </div>

              {/* Step Nodes */}
              <div className="absolute inset-0 flex items-center justify-center">
                
                {/* Step 01: Discovery */}
                <motion.div 
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true, amount: 0.2 }}
                  className="absolute -translate-y-[130px] -translate-x-[90px] md:-translate-y-[280px] md:-translate-x-[280px] z-30"
                >
                  <div className="group relative flex flex-col items-center">
                    <div className="w-10 h-10 md:w-24 md:h-24 rounded-xl md:rounded-3xl bg-gradient-to-br from-gray-900 to-black border border-blue-500/40 flex items-center justify-center group-hover:border-blue-500 transition-all duration-500 shadow-[0_0_30px_rgba(59,130,246,0.15)] group-hover:shadow-[0_0_50px_rgba(59,130,246,0.4)]">
                      <div className="absolute -top-1.5 -left-1.5 w-5 h-5 md:w-10 md:h-10 rounded-md md:rounded-xl bg-blue-600 flex items-center justify-center text-[10px] md:text-[16px] font-black shadow-xl">01</div>
                      <BookOpen className="w-5 h-5 md:w-12 md:h-12 text-white group-hover:text-blue-400 transition-colors" />
                    </div>
                    <div className="absolute top-full mt-3 md:mt-8 text-center w-[100px] md:w-[260px] opacity-0 group-hover:opacity-100 transition-all duration-500 translate-y-2 group-hover:translate-y-0">
                      <h4 className="text-[14px] md:text-[22px] font-black mb-0.5 md:mb-2 uppercase tracking-tight text-blue-400">{t("Discovery")}</h4>
                      <p className="text-[13px] md:text-[16px] text-white/80 leading-tight md:leading-relaxed font-medium">{t("Identify ArtTech goals through expert consultation.")}</p>
                    </div>
                    {/* Visual Connector to Core */}
                    <div className="absolute top-1/2 left-1/2 w-[40px] md:w-[200px] h-px bg-gradient-to-r from-blue-500/60 to-transparent origin-left rotate-[135deg] pointer-events-none" />
                  </div>
                </motion.div>

                {/* Step 02: Strategy */}
                <motion.div 
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true, amount: 0.2 }}
                  className="absolute -translate-y-[160px] md:-translate-y-[350px] z-30"
                >
                  <div className="group relative flex flex-col items-center">
                    <div className="w-10 h-10 md:w-24 md:h-24 rounded-xl md:rounded-3xl bg-gradient-to-br from-gray-900 to-black border border-blue-500/40 flex items-center justify-center group-hover:border-blue-500 transition-all duration-500 shadow-[0_0_30px_rgba(59,130,246,0.15)] group-hover:shadow-[0_0_50px_rgba(59,130,246,0.4)]">
                      <div className="absolute -top-1.5 -left-1.5 w-5 h-5 md:w-10 md:h-10 rounded-md md:rounded-xl bg-blue-600 flex items-center justify-center text-[10px] md:text-[16px] font-black shadow-xl">02</div>
                      <Layers className="w-5 h-5 md:w-12 md:h-12 text-white group-hover:text-blue-400 transition-colors" />
                    </div>
                    <div className="absolute top-full mt-3 md:mt-8 text-center w-[100px] md:w-[260px] opacity-0 group-hover:opacity-100 transition-all duration-500 translate-y-2 group-hover:translate-y-0">
                      <h4 className="text-[14px] md:text-[22px] font-black mb-0.5 md:mb-2 uppercase tracking-tight text-blue-400">{t("Strategy")}</h4>
                      <p className="text-[13px] md:text-[16px] text-white/80 leading-tight md:leading-relaxed font-medium">{t("Receive a custom EdTech roadmap or STEAM plan.")}</p>
                    </div>
                    {/* Visual Connector to Core */}
                    <div className="absolute top-1/2 left-1/2 w-[60px] md:w-[300px] h-px bg-gradient-to-r from-blue-500/60 to-transparent origin-left rotate-[90deg] pointer-events-none" />
                  </div>
                </motion.div>

                {/* Step 03: Launch */}
                <motion.div 
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true, amount: 0.2 }}
                  className="absolute -translate-y-[130px] translate-x-[90px] md:-translate-y-[280px] md:translate-x-[280px] z-30"
                >
                  <div className="group relative flex flex-col items-center">
                    <div className="w-10 h-10 md:w-24 md:h-24 rounded-xl md:rounded-3xl bg-gradient-to-br from-gray-900 to-black border border-blue-500/40 flex items-center justify-center group-hover:border-blue-500 transition-all duration-500 shadow-[0_0_30px_rgba(59,130,246,0.15)] group-hover:shadow-[0_0_50px_rgba(59,130,246,0.4)]">
                      <div className="absolute -top-1.5 -left-1.5 w-5 h-5 md:w-10 md:h-10 rounded-md md:rounded-xl bg-blue-600 flex items-center justify-center text-[10px] md:text-[16px] font-black shadow-xl">03</div>
                      <Rocket className="w-5 h-5 md:w-12 md:h-12 text-white group-hover:text-blue-400 transition-colors" />
                    </div>
                    <div className="absolute top-full mt-3 md:mt-8 text-center w-[100px] md:w-[260px] opacity-0 group-hover:opacity-100 transition-all duration-500 translate-y-2 group-hover:translate-y-0">
                      <h4 className="text-[14px] md:text-[22px] font-black mb-0.5 md:mb-2 uppercase tracking-tight text-blue-400">{t("Launch")}</h4>
                      <p className="text-[13px] md:text-[16px] text-white/80 leading-tight md:leading-relaxed font-medium">{t("Implement the curriculum or start building prototypes.")}</p>
                    </div>
                    {/* Visual Connector to Core */}
                    <div className="absolute top-1/2 left-1/2 w-[40px] md:w-[200px] h-px bg-gradient-to-r from-blue-500/60 to-transparent origin-left rotate-[45deg] pointer-events-none" />
                  </div>
                </motion.div>

              </div>

              {/* Background Data Streams */}
              <div className="absolute inset-0 pointer-events-none opacity-20">
                <motion.div 
                  animate={{ 
                    rotate: 360,
                    scale: [1, 1.1, 1]
                  }}
                  transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
                  className="absolute inset-0 border-[0.5px] border-blue-500/10 rounded-full scale-[1.5]"
                />
              </div>

            </div>
          </div>
        </div>
      </section>

      <QefResourceCenter onBookConsultation={() => openContactModal(t("Book Consultation"), t("Educator Support"), t("QEF Resource Center"), "blue")} t={t} />

      {/* For Families and Individuals Section */}
      <section id="for-families" className="relative z-10 bg-black py-16 md:py-[120px] px-6 md:px-[60px] lg:px-[120px] overflow-hidden">
        {/* ARTtech Generative Background */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {/* Subtle Geometric Pattern - Cleaner */}
          <div className="absolute inset-0 opacity-[0.06]" 
               style={{ 
                 backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg stroke='%23ffffff' stroke-width='0.5' stroke-opacity='0.2'%3E%3Cpath d='M50 50 L100 50 M50 50 L50 100 M50 50 L0 50 M50 50 L50 0'/%3E%3Ccircle cx='50' cy='50' r='2' fill='%23ffffff' fill-opacity='0.1'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                 backgroundSize: '100px 100px'
               }} 
          />
          
          {/* Abstract Moving Lines - More Visible */}
          <svg className="absolute inset-0 w-full h-full opacity-[0.2]" xmlns="http://www.w3.org/2000/svg">
            <motion.path
              d="M-100 200 Q 500 400 1100 200 T 2300 200"
              fill="none"
              stroke="white"
              strokeWidth="1"
              animate={{
                d: [
                  "M-100 200 Q 500 400 1100 200 T 2300 200",
                  "M-100 250 Q 550 350 1150 250 T 2350 250",
                  "M-100 200 Q 500 400 1100 200 T 2300 200"
                ]
              }}
              transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }}
            />
            <motion.path
              d="M-100 700 Q 600 500 1200 700 T 2500 700"
              fill="none"
              stroke="white"
              strokeWidth="1"
              animate={{
                d: [
                  "M-100 700 Q 600 500 1200 700 T 2500 700",
                  "M-100 650 Q 650 550 1250 650 T 2550 650",
                  "M-100 700 Q 600 500 1200 700 T 2500 700"
                ]
              }}
              transition={{ duration: 18, repeat: Infinity, ease: "easeInOut", delay: 1 }}
            />
          </svg>

          {/* Floating Geometric Outlines - More Defined */}
          {[...Array(6)].map((_, i) => (
            <motion.div
              key={i}
              animate={{ 
                y: [0, -60, 0],
                rotate: [0, 90, 180],
                opacity: [0.05, 0.12, 0.05],
                scale: [1, 1.1, 1]
              }}
              transition={{ 
                duration: 12 + i * 3, 
                repeat: Infinity, 
                ease: "easeInOut"
              }}
              className="absolute border border-white/20"
              style={{
                width: `${60 + i * 30}px`,
                height: `${60 + i * 30}px`,
                left: `${(i * 20) % 100}%`,
                top: `${(i * 25) % 100}%`,
                borderRadius: i % 3 === 0 ? '0%' : i % 3 === 1 ? '50%' : '12px',
                transform: `rotate(${i * 60}deg)`
              }}
            />
          ))}

          {/* Subtle Gradient Glows (Clean, no noise) */}
          <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-purple-500/10 blur-[120px] rounded-full pointer-events-none" />
          <div className="absolute bottom-0 right-1/4 w-[600px] h-[600px] bg-blue-500/10 blur-[150px] rounded-full pointer-events-none" />
        </div>
        
        <div className="max-w-[1400px] mx-auto relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-start">
            
            {/* Left Column: Content */}
            <div className="flex flex-col gap-12">
              <div className="flex flex-col gap-6">
                <div className="flex items-center gap-4">
                  <div className="px-3 py-1 rounded-full bg-purple-500/10 border border-purple-500/20">
                    <span className="text-[12px] font-bold uppercase tracking-[0.2em] text-purple-400">{t("For Families and Individuals")}</span>
                  </div>
                  <span className="text-[14px] font-bold uppercase tracking-[0.4em] text-purple-400/60">{t("University-Backed Programs")}</span>
                </div>
                <div className="box">
                  <h2 className="text-[32px] sm:text-[48px] md:text-[72px] font-medium leading-[1] tracking-tight text-purple-400">
                    {t("Bridge the")} <br />
                    <span className="italic font-serif text-purple-200">{t("Tech Gap")}</span>
                  </h2>
                </div>
                <p className="text-[20px] text-white/60 leading-relaxed max-w-[540px]">
                  {t("Trusted by schools, associations, and NGOs, and guided by an advisory board of university professors. We turn screen time into family co-creation.")}
                </p>
                
                <div className="mt-4 p-6 rounded-2xl bg-purple-500/10 border border-purple-500/30 flex flex-col items-start gap-4">
                  <h3 className="text-[24px] font-bold text-white">{t("課程現正招生")}</h3>
                  <a 
                    href="https://linktr.ee/CreaTechInnovationAcademy" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center px-8 py-4 rounded-full bg-purple-500 text-white font-bold text-[16px] hover:bg-purple-600 transition-colors"
                  >
                    {t("速速報名")}
                  </a>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Feature 1 */}
                <HighTechCard className="rounded-2xl bg-titanium border-silver" spotlightColor="rgba(168,85,247,0.15)">
                  <div className="flex flex-col gap-4 p-5 transition-all group h-full">
                    <div className="w-12 h-12 shrink-0 rounded-xl bg-purple-500/20 flex items-center justify-center text-purple-400 group-hover:scale-110 transition-transform">
                      <BookOpen className="w-6 h-6" />
                    </div>
                    <div className="flex flex-col gap-2 flex-grow">
                      <h4 className="text-[18px] font-bold text-white leading-tight">{t("AI Beginner Courses")}</h4>
                      <ul className="text-[14px] text-white/50 leading-relaxed space-y-1 list-disc pl-4">
                        <li>{t("Step-by-step foundational learning")}</li>
                        <li>{t("Master generative AI tools")}</li>
                        <li>{t("Boost daily productivity")}</li>
                      </ul>
                    </div>
                    <button onClick={() => openContactModal(t("Inquire Now"), "For Families and Individuals", "AI Beginner Courses", "purple")} className="mt-2 w-fit px-4 py-1.5 rounded-full border border-purple-500/50 text-purple-400 hover:bg-purple-500/10 transition-colors text-xs font-bold uppercase tracking-wider">
                      {t("Inquire Now")}
                    </button>
                  </div>
                </HighTechCard>

                {/* Feature 2 */}
                <HighTechCard className="rounded-2xl bg-titanium border-silver" spotlightColor="rgba(168,85,247,0.15)">
                  <div className="flex flex-col gap-4 p-5 transition-all group h-full">
                    <div className="w-12 h-12 shrink-0 rounded-xl bg-purple-500/20 flex items-center justify-center text-purple-400 group-hover:scale-110 transition-transform">
                      <Users className="w-6 h-6" />
                    </div>
                    <div className="flex flex-col gap-2 flex-grow">
                      <h4 className="text-[18px] font-bold text-white leading-tight">{t("Parent-Child AI Co-Creation")}</h4>
                      <ul className="text-[14px] text-white/50 leading-relaxed space-y-1 list-disc pl-4">
                        <li>{t("Interactive family workshops")}</li>
                        <li>{t("Collaborate on stories & art")}</li>
                        <li>{t("Turn screen time into bonding")}</li>
                      </ul>
                    </div>
                    <button onClick={() => openContactModal(t("Inquire Now"), "For Families and Individuals", "Parent-Child AI Co-Creation", "purple")} className="mt-2 w-fit px-4 py-1.5 rounded-full border border-purple-500/50 text-purple-400 hover:bg-purple-500/10 transition-colors text-xs font-bold uppercase tracking-wider">
                      {t("Inquire Now")}
                    </button>
                  </div>
                </HighTechCard>

                {/* Feature 3 */}
                <HighTechCard className="rounded-2xl bg-titanium border-silver" spotlightColor="rgba(168,85,247,0.15)">
                  <div className="flex flex-col gap-4 p-5 transition-all group h-full">
                    <div className="w-12 h-12 shrink-0 rounded-xl bg-purple-500/20 flex items-center justify-center text-purple-400 group-hover:scale-110 transition-transform">
                      <Box className="w-6 h-6" />
                    </div>
                    <div className="flex flex-col gap-2 flex-grow">
                      <h4 className="text-[18px] font-bold text-white leading-tight">{t("AI to Object Class")}</h4>
                      <ul className="text-[14px] text-white/50 leading-relaxed space-y-1 list-disc pl-4">
                        <li>{t("Digital AI to physical objects")}</li>
                        <li>{t("3D printing & laser cutting")}</li>
                        <li>{t("Hands-on maker experience")}</li>
                      </ul>
                    </div>
                    <button onClick={() => openContactModal(t("Inquire Now"), "For Families and Individuals", "AI to Object Class", "purple")} className="mt-2 w-fit px-4 py-1.5 rounded-full border border-purple-500/50 text-purple-400 hover:bg-purple-500/10 transition-colors text-xs font-bold uppercase tracking-wider">
                      {t("Inquire Now")}
                    </button>
                  </div>
                </HighTechCard>

                {/* Feature 4 */}
                <HighTechCard className="rounded-2xl bg-titanium border-silver" spotlightColor="rgba(168,85,247,0.15)">
                  <div className="flex flex-col gap-4 p-5 transition-all group h-full">
                    <div className="w-12 h-12 shrink-0 rounded-xl bg-purple-500/20 flex items-center justify-center text-purple-400 group-hover:scale-110 transition-transform">
                      <Plane className="w-6 h-6" />
                    </div>
                    <div className="flex flex-col gap-2 flex-grow">
                      <h4 className="text-[18px] font-bold text-white leading-tight">{t("Drone Class")}</h4>
                      <ul className="text-[14px] text-white/50 leading-relaxed space-y-1 list-disc pl-4">
                        <li>{t("Learn to program & fly drones")}</li>
                        <li>{t("Combine coding with real-world")}</li>
                        <li>{t("Exciting aerial challenges")}</li>
                      </ul>
                    </div>
                    <button onClick={() => openContactModal(t("Inquire Now"), "For Families and Individuals", "Drone Class", "purple")} className="mt-2 w-fit px-4 py-1.5 rounded-full border border-purple-500/50 text-purple-400 hover:bg-purple-500/10 transition-colors text-xs font-bold uppercase tracking-wider">
                      {t("Inquire Now")}
                    </button>
                  </div>
                </HighTechCard>
              </div>
            </div>

            {/* Right Column: Visuals */}
            <div className="relative">
              <div className="aspect-[3/4] rounded-[40px] overflow-hidden border-silver shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1531482615713-2afd69097998?auto=format&fit=crop&q=80&w=1200" 
                  alt="HK Family Learning"
                  className="w-full h-full object-cover grayscale-[0.2] hover:grayscale-0 transition-all duration-1000"
                  referrerPolicy="no-referrer"
                />
              </div>
              {/* Floating Badge */}
              <motion.div 
                animate={{ y: [0, -20, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                className="absolute bottom-8 left-8 bg-white p-8 rounded-[32px] shadow-2xl hidden md:block z-20"
              >
                <div className="flex flex-col gap-1">
                  <span className="text-black/40 text-[12px] uppercase font-bold tracking-widest">{t("Guided by")}</span>
                  <span className="text-black text-[20px] font-serif italic font-bold">{t("University Professors")}</span>
                </div>
              </motion.div>
            </div>

          </div>
        </div>
      </section>

      {/* For NGOs and Associations Section */}
      <section id="for-ngos" className="relative z-10 bg-black py-16 md:py-[120px] px-6 md:px-[60px] lg:px-[120px] overflow-hidden">
        {/* Digital Grid & Scanline Background */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute inset-0 bg-grid opacity-[0.15]" />
          {/* Vertical Data Streams */}
          {[...Array(15)].map((_, i) => (
            <motion.div
              key={i}
              initial={{ y: "-100%" }}
              animate={{ y: "200%" }}
              transition={{ 
                duration: 10 + Math.random() * 15, 
                repeat: Infinity, 
                ease: "linear",
                delay: Math.random() * 10
              }}
              className="absolute w-px h-[300px] bg-gradient-to-b from-transparent via-emerald-500/30 to-transparent"
              style={{ left: `${(i * 7) % 100}%` }}
            />
          ))}
          {/* Horizontal Scanline */}
          <motion.div 
            animate={{ y: ["0%", "100%"] }}
            transition={{ duration: 12, repeat: Infinity, ease: "linear" }}
            className="absolute inset-x-0 h-px bg-emerald-500/20 shadow-[0_0_15px_rgba(16,185,129,0.5)] z-10"
          />
          {/* Glowing Data Points */}
          {[...Array(8)].map((_, i) => (
            <motion.div
              key={i}
              animate={{ 
                opacity: [0.1, 0.6, 0.1],
                scale: [1, 1.5, 1]
              }}
              transition={{ 
                duration: 3 + Math.random() * 4, 
                repeat: Infinity, 
                ease: "easeInOut",
                delay: i * 0.8
              }}
              className="absolute w-1 h-1 bg-emerald-400 rounded-full shadow-[0_0_8px_rgba(52,211,153,0.8)]"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
            />
          ))}
        </div>
        {/* Technical Grid Background */}
        <div className="absolute inset-0 bg-grid opacity-10 pointer-events-none" />
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-emerald-500/30 to-transparent" />
        
        <div className="max-w-[1400px] mx-auto relative z-10">
          <div className="flex flex-col gap-20">
            
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-end gap-10">
              <div className="flex flex-col gap-6">
                <div className="flex items-center gap-4">
                  <div className="px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20">
                    <span className="text-[12px] font-bold uppercase tracking-[0.2em] text-emerald-400">{t("For NGOs & Associations")}</span>
                  </div>
                  <span className="text-[14px] font-bold uppercase tracking-[0.4em] text-emerald-400/60">{t("AI Literacy & Co-Creation")}</span>
                </div>
                <div className="box">
                  <h2 className="text-[32px] sm:text-[48px] md:text-[80px] font-medium leading-[0.9] tracking-tighter uppercase text-emerald-400">
                    {t("Empowering")} <br />
                    <span className="text-emerald-400">{t("Communities")}</span>
                  </h2>
                </div>
                <p className="text-[20px] text-white/40 leading-relaxed max-w-[540px]">
                  {t("Partner with us to bring AI literacy, co-creation projects, intergenerational programs, and professional group training to your community.")}
                </p>
                
                <motion.button 
                  whileHover={{ scale: 1.1, y: -5 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => openContactModal(t("Book Now"), "For NGOs & Associations", "General Inquiry", "emerald")}
                  className="relative group overflow-hidden rounded-full w-fit mt-4"
                >
                  <div className="absolute inset-0 bg-emerald-600 translate-y-full group-hover:translate-y-0 transition-transform duration-500" />
                  <div className="relative border border-white/20 px-10 py-5 rounded-full flex items-center gap-4 transition-colors group-hover:border-transparent group-hover:text-white">
                    <span className="text-[16px] font-bold uppercase tracking-widest">{t("Book Now")}</span>
                    <ArrowUpRight className="w-5 h-5 group-hover:rotate-45 transition-transform duration-500" />
                  </div>
                </motion.button>
              </div>
              
              <motion.div 
                whileHover={{ scale: 1.05 }}
                className="flex flex-col gap-4 items-end cursor-pointer"
              >
                <div className="flex items-center gap-3 text-emerald-400/80">
                  <ShieldCheck className="w-5 h-5" />
                  <span className="text-[14px] font-mono uppercase tracking-widest">{t("Social Inclusion Certified")}</span>
                </div>
                <div className="text-[13px] font-mono text-white/30 text-right max-w-[200px]">
                  {t("TRACKED BY UCLA LONELINESS SCALE TO REDUCE SOCIAL ISOLATION")}
                </div>
              </motion.div>
            </div>

            {/* Main Content Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              
              {/* AI Literacy */}
              <HighTechCard className="bg-titanium p-10 flex flex-col gap-6 border-silver rounded-2xl" spotlightColor="rgba(16,185,129,0.15)">
                <div className="w-12 h-12 rounded-lg bg-emerald-500/10 flex items-center justify-center text-emerald-400">
                  <BookOpen className="w-6 h-6" />
                </div>
                <h3 className="text-[24px] font-bold text-white uppercase tracking-tight">{t("AI Literacy Courses")}</h3>
                <p className="text-[16px] text-white/50 leading-relaxed">
                  {t("Comprehensive AI training programs designed to demystify artificial intelligence. We equip your community with the foundational knowledge and practical skills needed to navigate and thrive in an AI-driven world.")}
                </p>
              </HighTechCard>

              {/* Co-creation */}
              <HighTechCard className="bg-titanium p-10 flex flex-col gap-6 border-silver rounded-2xl" spotlightColor="rgba(16,185,129,0.15)">
                <div className="w-12 h-12 rounded-lg bg-emerald-500/10 flex items-center justify-center text-emerald-400">
                  <Palette className="w-6 h-6" />
                </div>
                <h3 className="text-[24px] font-bold text-white uppercase tracking-tight">{t("Co-Creation Projects")}</h3>
                <p className="text-[16px] text-white/50 leading-relaxed">
                  {t("Collaborative initiatives where technology meets community needs. We partner with NGOs to design and implement tech-driven solutions that address real-world challenges, fostering innovation from the ground up.")}
                </p>
              </HighTechCard>

              {/* Intergenerational Stream */}
              <HighTechCard className="bg-titanium p-10 flex flex-col gap-6 border-silver rounded-2xl" spotlightColor="rgba(16,185,129,0.15)">
                <div className="w-12 h-12 rounded-lg bg-emerald-500/10 flex items-center justify-center text-emerald-400">
                  <Users className="w-6 h-6" />
                </div>
                <h3 className="text-[24px] font-bold text-white uppercase tracking-tight">{t("Intergenerational Stream")}</h3>
                <p className="text-[16px] text-white/50 leading-relaxed">
                  {t("Bridging the age gap through shared technological experiences. Our programs bring youth and seniors together, promoting mutual understanding, reducing social isolation, and preserving cultural heritage through digital mediums.")}
                </p>
              </HighTechCard>

              {/* Group Training for Professionals */}
              <HighTechCard className="bg-titanium p-10 flex flex-col gap-6 border-silver rounded-2xl" spotlightColor="rgba(16,185,129,0.15)">
                <div className="w-12 h-12 rounded-lg bg-emerald-500/10 flex items-center justify-center text-emerald-400">
                  <Briefcase className="w-6 h-6" />
                </div>
                <h3 className="text-[24px] font-bold text-white uppercase tracking-tight">{t("Group Training for Professionals")}</h3>
                <p className="text-[16px] text-white/50 leading-relaxed">
                  {t("Tailored upskilling workshops for NGO staff and social workers. We provide hands-on training in the latest digital tools and AI applications, empowering your team to work more efficiently and amplify their impact.")}
                </p>
              </HighTechCard>

            </div>

            {/* Impact Dashboard */}
            <div className="bg-titanium border-silver p-10 md:p-16 rounded-2xl relative overflow-hidden">
              <div className="absolute top-0 right-0 p-8 opacity-5">
                <BarChart3 className="w-64 h-64" />
              </div>
              
              <div className="relative z-10 flex flex-col gap-12">
                <div className="flex flex-col gap-2">
                  <span className="text-[14px] font-mono text-emerald-400 uppercase tracking-[0.3em]">{t("Impact Metrics")}</span>
                  <h3 className="text-[32px] font-bold text-white">{t("Measurable Community Impact")}</h3>
                </div>

                <motion.button 
                  whileHover={{ scale: 1.1, y: -5 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => openContactModal(t("Partner With Us"), t("For NGOs & Associations"), t("Partnership"), "emerald")}
                  className="relative group overflow-hidden rounded-full w-fit"
                >
                  <div className="absolute inset-0 bg-emerald-600 translate-y-full group-hover:translate-y-0 transition-transform duration-500" />
                  <div className="relative border border-white/20 px-10 py-5 rounded-full flex items-center gap-4 transition-colors group-hover:border-transparent group-hover:text-white">
                    <span className="text-[16px] font-bold uppercase tracking-widest">{t("Partner With Us")}</span>
                    <ArrowUpRight className="w-5 h-5 group-hover:rotate-45 transition-transform duration-500" />
                  </div>
                </motion.button>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
                  {/* Metric 1 */}
                  <HighTechCard className="rounded-xl bg-titanium border-silver" spotlightColor="rgba(16,185,129,0.15)">
                    <div className="flex flex-col gap-4 p-8">
                      <span className="text-[48px] font-mono font-bold text-white leading-none">700+</span>
                      <div className="flex flex-col gap-1">
                        <span className="text-[16px] font-bold text-white uppercase tracking-tight">{t("Young-old Empowered")}</span>
                        <p className="text-[14px] text-white/40 leading-relaxed">
                          {t("Guided to confidently publish AI memoirs and digital art.")}
                        </p>
                      </div>
                    </div>
                  </HighTechCard>

                  {/* Metric 2 */}
                  <HighTechCard className="rounded-xl bg-titanium border-silver" spotlightColor="rgba(16,185,129,0.15)">
                    <div className="flex flex-col gap-4 p-8">
                      <span className="text-[48px] font-mono font-bold text-white leading-none">300+</span>
                      <div className="flex flex-col gap-1">
                        <span className="text-[16px] font-bold text-white uppercase tracking-tight">{t("Youth Mentors")}</span>
                        <p className="text-[14px] text-white/40 leading-relaxed">
                          {t("A growing network trained to bridge the digital divide.")}
                        </p>
                      </div>
                    </div>
                  </HighTechCard>

                  {/* Metric 3 */}
                  <HighTechCard className="rounded-xl bg-titanium border-silver" spotlightColor="rgba(16,185,129,0.15)">
                    <div className="flex flex-col gap-4 p-8">
                      <div className="flex items-center gap-3">
                        <span className="text-[48px] font-mono font-bold text-white leading-none">{t("UCLA")}</span>
                        <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                      </div>
                      <div className="flex flex-col gap-1">
                        <span className="text-[16px] font-bold text-white uppercase tracking-tight">{t("Loneliness Scale")}</span>
                        <p className="text-[14px] text-white/40 leading-relaxed">
                          {t("Scientifically tracked social inclusion and isolation reduction.")}
                        </p>
                      </div>
                    </div>
                  </HighTechCard>
                </div>

                {/* Flagship Project Example */}
                <div className="mt-16 pt-16 border-t border-white/10">
                  <div className="flex flex-col gap-4 mb-10">
                    <span className="text-[14px] font-mono text-emerald-400 uppercase tracking-[0.3em]">{t("Flagship Project Example & Outcomes")}</span>
                    <h3 className="text-[28px] md:text-[36px] font-bold text-white leading-tight">
                      {t("Digital Heritage: Recreating History with AI")}
                    </h3>
                    <p className="text-[18px] text-white/60 leading-relaxed max-w-4xl">
                      {t("Students used AI video generation and voice cloning to recreate the life of Hilton Cheong-Leen (\"The Father of HK Hawkers\").")} 
                      <br/><br/>
                      <strong className="text-emerald-400">{t("Outcome:")}</strong>{t(" A stunning, high-value humanities PBL (Project-Based Learning) portfolio piece that preserves local culture.")}
                    </p>
                    <ul className="mt-4 space-y-3">
                      <li className="flex items-start gap-3 text-white/70">
                        <CheckCircle2 className="w-6 h-6 text-emerald-500 shrink-0" />
                        <span className="text-[16px]">{t("We have guided over ")}<strong>{t("40 young-olds")}</strong>{t(" to do the chapter on their own to combine into a mini-film.")}</span>
                      </li>
                      <li className="flex items-start gap-3 text-white/70">
                        <CheckCircle2 className="w-6 h-6 text-emerald-500 shrink-0" />
                        <span className="text-[16px]"><strong>{t("Intergenerational Connection:")}</strong>{t(" Fostered meaningful collaboration between youth and elders through shared digital creation.")}</span>
                      </li>
                      <li className="flex items-start gap-3 text-white/70">
                        <CheckCircle2 className="w-6 h-6 text-emerald-500 shrink-0" />
                        <span className="text-[16px]"><strong>{t("Digital Literacy:")}</strong>{t(" Empowered seniors with practical AI skills, reducing the digital divide and enhancing social inclusion.")}</span>
                      </li>
                    </ul>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="aspect-video rounded-2xl overflow-hidden border border-white/10 shadow-2xl">
                      <iframe 
                        width="100%" height="100%" 
                        src="https://www.youtube.com/embed/iQs99darEJ0" 
                        title="YouTube video player" frameBorder="0" 
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                        allowFullScreen>
                      </iframe>
                    </div>
                    <div className="aspect-video rounded-2xl overflow-hidden border border-white/10 shadow-2xl">
                      <iframe 
                        width="100%" height="100%" 
                        src="https://www.youtube.com/embed/AcxQvfOZKAA" 
                        title="YouTube video player" frameBorder="0" 
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                        allowFullScreen>
                      </iframe>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Take Your Next Step Section */}
      <section className="relative z-10 bg-[#050505] py-24 px-6 md:px-[60px] lg:px-[120px] border-t border-white/10 overflow-hidden">
        {/* Background Accent */}
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-gradient-to-br from-blue-500/5 via-purple-500/5 to-emerald-500/5 blur-[120px] rounded-full -translate-y-1/2 translate-x-1/2" />
        
        <div className="max-w-[1400px] mx-auto">
          <div className="flex flex-col lg:flex-row gap-16 items-start">
            {/* CTAs Grid */}
            <div className="flex-1 flex flex-col gap-12">
              <div className="box">
                <motion.h2 
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="text-[32px] sm:text-[48px] md:text-[64px] font-bold text-white leading-[0.9] tracking-tighter"
                >
                  {t("Take Your ")}<span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-purple-400 to-emerald-400">{t("Next Step")}</span>{t(" With Us")}
                </motion.h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* School CTA */}
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  whileHover={{ 
                    scale: 1.05, 
                    y: -10,
                    boxShadow: "0 25px 50px -12px rgba(59, 130, 246, 0.25)"
                  }}
                  whileTap={{ scale: 0.95 }}
                  transition={{ 
                    type: "spring", 
                    stiffness: 400, 
                    damping: 17,
                    delay: 0.1 
                  }}
                  className="group p-8 rounded-2xl bg-titanium border-silver hover:border-blue-500/50 transition-colors duration-500"
                >
                  <div className="text-[32px] mb-4">🎓</div>
                  <h3 className="text-[20px] font-bold text-white mb-2">{t("Schools")}</h3>
                  <p className="text-[16px] text-white/60 leading-relaxed mb-6">
                    {t("Book a Free QEF Strategy Consultation to elevate your curriculum.")}
                  </p>
                  <button onClick={() => openContactModal(t("Book Now"), t("For Schools"), "QEF Strategy Consultation", "blue")} className="inline-flex items-center gap-2 text-[14px] font-bold uppercase tracking-widest text-blue-500 group-hover:gap-4 transition-all">
                    {t("Book Now")} <ArrowRight className="w-4 h-4" />
                  </button>
                </motion.div>

                {/* Parent CTA */}
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  whileHover={{ 
                    scale: 1.05, 
                    y: -10,
                    boxShadow: "0 25px 50px -12px rgba(168, 85, 247, 0.25)"
                  }}
                  whileTap={{ scale: 0.95 }}
                  transition={{ 
                    type: "spring", 
                    stiffness: 400, 
                    damping: 17,
                    delay: 0.2 
                  }}
                  className="group p-8 rounded-2xl bg-titanium border-silver hover:border-purple-500/50 transition-colors duration-500"
                >
                  <div className="text-[32px] mb-4">👨‍👩‍👧</div>
                  <h3 className="text-[20px] font-bold text-white mb-2">{t("Parents")}</h3>
                  <p className="text-[16px] text-white/60 leading-relaxed mb-6">
                    {t("Enroll in Parent-Child AI & Drone Bootcamps for a future-ready family.")}
                  </p>
                  <button onClick={() => openContactModal(t("Enroll Today"), t("For Families"), "Parent-Child Bootcamps", "purple")} className="inline-flex items-center gap-2 text-[14px] font-bold uppercase tracking-widest text-purple-500 group-hover:gap-4 transition-all">
                    {t("Enroll Today")} <ArrowRight className="w-4 h-4" />
                  </button>
                </motion.div>

                {/* Association CTA */}
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  whileHover={{ 
                    scale: 1.05, 
                    y: -10,
                    boxShadow: "0 25px 50px -12px rgba(16, 185, 129, 0.25)"
                  }}
                  whileTap={{ scale: 0.95 }}
                  transition={{ 
                    type: "spring", 
                    stiffness: 400, 
                    damping: 17,
                    delay: 0.3 
                  }}
                  className="group p-8 rounded-2xl bg-titanium border-silver hover:border-emerald-500/50 transition-colors duration-500"
                >
                  <div className="text-[32px] mb-4">🤝</div>
                  <h3 className="text-[20px] font-bold text-white mb-2">{t("Associations")}</h3>
                  <p className="text-[16px] text-white/60 leading-relaxed mb-6">
                    {t("Partner on CSR & SIE Fund Projects to create lasting social impact.")}
                  </p>
                  <button onClick={() => openContactModal(t("Partner With Us"), t("For NGOs"), "CSR & SIE Fund Projects", "emerald")} className="inline-flex items-center gap-2 text-[14px] font-bold uppercase tracking-widest text-emerald-500 group-hover:gap-4 transition-all">
                    {t("Partner With Us")} <ArrowRight className="w-4 h-4" />
                  </button>
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="relative z-10 bg-[#050505] py-24 px-6 md:px-[60px] lg:px-[120px] border-t border-white/10 overflow-hidden">
        <div className="max-w-[1000px] mx-auto">
          <div className="flex flex-col gap-12">
            <div className="flex flex-col gap-4 text-center items-center">
              <span className="text-[14px] font-mono text-purple-400 uppercase tracking-[0.3em]">{t("FAQ")}</span>
              <h2 className="text-[32px] sm:text-[48px] font-bold text-white leading-[1.1] tracking-tight">
                {t("Frequently Asked Questions")}
              </h2>
            </div>
            
            <div className="flex flex-col gap-4">
              {[
                {
                  q: t("What age groups are your programs suitable for?"),
                  a: t("Our programs are designed for a wide range of ages, from primary school students (with parent-child co-creation options) to secondary students, young adults, and even seniors in our intergenerational streams.")
                },
                {
                  q: t("Do I need prior coding or AI experience?"),
                  a: t("Not at all! Our beginner courses are designed to be accessible to everyone, regardless of technical background. We guide you step-by-step from the basics to advanced applications.")
                },
                {
                  q: t("Are your programs available for schools outside of Hong Kong?"),
                  a: t("Currently, our primary focus is on schools and organizations within Hong Kong, but we are open to discussing virtual partnerships or licensing our curriculum internationally. Please contact us for more details.")
                },
                {
                  q: t("How do I enroll in a course?"),
                  a: t("You can enroll directly through our Linktree for family and individual courses, or contact us via the 'Book Now' or 'Partner With Us' buttons for school and NGO programs.")
                }
              ].map((faq, i) => (
                <div 
                  key={i} 
                  className="p-6 rounded-2xl bg-titanium border border-silver hover:border-purple-500/30 transition-colors cursor-pointer"
                  onClick={() => setOpenFaqIndex(openFaqIndex === i ? null : i)}
                >
                  <div className="flex justify-between items-center">
                    <h4 className="text-[18px] font-bold text-white mb-0">{faq.q}</h4>
                    {openFaqIndex === i ? (
                      <ChevronUp className="w-5 h-5 text-purple-400 shrink-0 ml-4" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-white/40 shrink-0 ml-4" />
                    )}
                  </div>
                  <AnimatePresence>
                    {openFaqIndex === i && (
                      <motion.div
                        initial={{ height: 0, opacity: 0, marginTop: 0 }}
                        animate={{ height: "auto", opacity: 1, marginTop: 16 }}
                        exit={{ height: 0, opacity: 0, marginTop: 0 }}
                        className="overflow-hidden"
                      >
                        <p className="text-[16px] text-white/60 leading-relaxed">{faq.a}</p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 bg-black py-24 px-6 md:px-[60px] lg:px-[120px] border-t border-white/10">
        <div className="max-w-[1400px] mx-auto flex flex-col lg:grid lg:grid-cols-4 gap-16 items-start">
          {/* Brand Column */}
          <div className="flex flex-col gap-8">
            <div className="flex items-center gap-4">
              <img 
                src="logo.png" 
                alt="Logo" 
                className="h-24 md:h-48 w-auto object-contain"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                }}
              />
            </div>
            <p className="text-white/40 text-[16px] leading-relaxed max-w-[300px]">
              {t("Hong Kong’s premier ArtTech partner providing turnkey EdTech curricula.")}
            </p>
          </div>

          {/* Hubs Column */}
          <div className="flex flex-col gap-8">
            <span className="text-[14px] font-bold uppercase tracking-[0.3em] text-white/60">{t("Our Hubs")}</span>
            <div className="flex flex-col gap-8">
                <motion.div 
                  whileHover={{ scale: 1.05, x: 5 }}
                  className="flex gap-4 group cursor-pointer"
                >
                  <span className="text-[20px] shrink-0">📍</span>
                  <div className="flex flex-col gap-1">
                    <span className="text-[18px] font-bold text-white group-hover:text-emerald-400 transition-colors">{t("Kwun Tong")}</span>
                    <span className="text-[16px] text-white/40 leading-tight">{t("AI Hub & Training Center")}</span>
                  </div>
                </motion.div>
                <motion.div 
                  whileHover={{ scale: 1.05, x: 5 }}
                  className="flex gap-4 group cursor-pointer"
                >
                  <span className="text-[20px] shrink-0">📍</span>
                  <div className="flex flex-col gap-1">
                    <span className="text-[18px] font-bold text-white group-hover:text-emerald-400 transition-colors">{t("Yuen Long")}</span>
                    <span className="text-[16px] text-white/40 leading-tight">{t("Community IoT & ArtTech Incubator")}</span>
                  </div>
                </motion.div>
            </div>
          </div>

          {/* Connect Column */}
          <div className="flex flex-col gap-8">
            <span className="text-[14px] font-bold uppercase tracking-[0.3em] text-white/60">{t("Connect")}</span>
            <div className="flex flex-col gap-6">
              <motion.a 
                whileHover={{ scale: 1.05, x: 5 }}
                href="https://www.createchacademy.com" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="flex items-center gap-3 text-[16px] text-white/40 hover:text-white transition-colors"
              >
                <Globe className="w-5 h-5 text-emerald-500" />
                www.createchacademy.com
              </motion.a>
              <motion.a 
                whileHover={{ scale: 1.05, x: 5 }}
                href="mailto:partnerships@createchacademy.com" 
                className="flex items-center gap-3 text-[16px] text-white/40 hover:text-white transition-colors"
              >
                <Mail className="w-5 h-5 text-emerald-500" />
                partnerships@createchacademy.com
              </motion.a>
              <motion.div 
                whileHover={{ scale: 1.05, x: 5 }}
                className="flex items-center gap-3 text-[16px] text-white/40"
              >
                <Smartphone className="w-5 h-5 text-emerald-500" />
                @CreaTechAcademy on IG & FB
              </motion.div>
            </div>
          </div>

          {/* Programs Column */}
          <div className="flex flex-col gap-8">
            <span className="text-[14px] font-bold uppercase tracking-[0.3em] text-white/60">{t("Programs")}</span>
            <div className="flex flex-col gap-4">
              <motion.a whileHover={{ scale: 1.1, x: 5 }} href="#for-schools" className="text-[16px] text-white/40 hover:text-white transition-colors inline-block origin-left">{t("For Schools")}</motion.a>
              <motion.a whileHover={{ scale: 1.1, x: 5 }} href="#for-families" className="text-[16px] text-white/40 hover:text-white transition-colors inline-block origin-left">{t("For Families and Individuals")}</motion.a>
              <motion.a whileHover={{ scale: 1.1, x: 5 }} href="#for-ngos" className="text-[16px] text-white/40 hover:text-white transition-colors inline-block origin-left">{t("For NGOs & Associations")}</motion.a>
            </div>
          </div>
        </div>

        <div className="max-w-[1400px] mx-auto mt-24 pt-8 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-6">
          <span className="text-[14px] text-white/20 font-mono tracking-wider">{t("© 2026 CREATECH INNOVATION ACADEMY. ALL RIGHTS RESERVED.")}</span>
          <div className="flex gap-12">
            <button onClick={(e) => { e.preventDefault(); setIsPrivacyModalOpen(true); }} className="text-[13px] font-bold uppercase tracking-widest text-white/20 hover:text-white transition-colors cursor-pointer">{t("Privacy Policy")}</button>
            <button onClick={(e) => { e.preventDefault(); setIsTermsModalOpen(true); }} className="text-[13px] font-bold uppercase tracking-widest text-white/20 hover:text-white transition-colors cursor-pointer">{t("Terms of Service")}</button>
          </div>
        </div>
      </footer>

      {/* Privacy Policy Modal */}
      <AnimatePresence>
        {isPrivacyModalOpen && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-6 bg-black/80 backdrop-blur-md"
            onClick={() => setIsPrivacyModalOpen(false)}
          >
            <motion.div 
              initial={{ scale: 0.95, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-titanium border border-silver rounded-3xl p-8 md:p-12 max-w-3xl w-full max-h-[80vh] overflow-y-auto relative shadow-2xl"
            >
              <button 
                onClick={() => setIsPrivacyModalOpen(false)}
                className="absolute top-6 right-6 w-10 h-10 rounded-full bg-white/5 flex items-center justify-center hover:bg-white/10 transition-colors text-white/60 hover:text-white"
              >
                ✕
              </button>
              <h2 className="text-[32px] font-bold text-white mb-6">{t("Privacy Policy")}</h2>
              <div className="text-white/70 space-y-4 text-[16px] leading-relaxed">
                <p><strong>Last Updated:</strong> March 2026</p>
                <p>Welcome to CreaTech Innovation Academy. We are committed to protecting your personal information and your right to privacy. If you have any questions or concerns about our policy, or our practices with regards to your personal information, please contact us.</p>
                <h3 className="text-[20px] font-bold text-white mt-6 mb-2">1. Information We Collect</h3>
                <p>We collect personal information that you voluntarily provide to us when registering at the Academy, expressing an interest in obtaining information about us or our products and services, when participating in activities on the Academy or otherwise contacting us.</p>
                <h3 className="text-[20px] font-bold text-white mt-6 mb-2">2. How We Use Your Information</h3>
                <p>We use personal information collected via our Academy for a variety of business purposes described below. We process your personal information for these purposes in reliance on our legitimate business interests, in order to enter into or perform a contract with you, with your consent, and/or for compliance with our legal obligations.</p>
                <h3 className="text-[20px] font-bold text-white mt-6 mb-2">3. Will Your Information Be Shared With Anyone?</h3>
                <p>We only share information with your consent, to comply with laws, to provide you with services, to protect your rights, or to fulfill business obligations.</p>
                <h3 className="text-[20px] font-bold text-white mt-6 mb-2">4. How Long Do We Keep Your Information?</h3>
                <p>We keep your information for as long as necessary to fulfill the purposes outlined in this privacy policy unless otherwise required by law.</p>
                <h3 className="text-[20px] font-bold text-white mt-6 mb-2">5. How Do We Keep Your Information Safe?</h3>
                <p>We aim to protect your personal information through a system of organizational and technical security measures.</p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Terms of Service Modal */}
      <AnimatePresence>
        {isTermsModalOpen && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-6 bg-black/80 backdrop-blur-md"
            onClick={() => setIsTermsModalOpen(false)}
          >
            <motion.div 
              initial={{ scale: 0.95, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-titanium border border-silver rounded-3xl p-8 md:p-12 max-w-3xl w-full max-h-[80vh] overflow-y-auto relative shadow-2xl"
            >
              <button 
                onClick={() => setIsTermsModalOpen(false)}
                className="absolute top-6 right-6 w-10 h-10 rounded-full bg-white/5 flex items-center justify-center hover:bg-white/10 transition-colors text-white/60 hover:text-white"
              >
                ✕
              </button>
              <h2 className="text-[32px] font-bold text-white mb-6">{t("Terms of Service")}</h2>
              <div className="text-white/70 space-y-4 text-[16px] leading-relaxed">
                <p><strong>Last Updated:</strong> March 2026</p>
                <p>These Terms of Service constitute a legally binding agreement made between you, whether personally or on behalf of an entity (“you”) and CreaTech Innovation Academy (“we,” “us” or “our”), concerning your access to and use of our website and services.</p>
                <h3 className="text-[20px] font-bold text-white mt-6 mb-2">1. Agreement to Terms</h3>
                <p>By accessing our services, you agree that you have read, understood, and agree to be bound by all of these Terms of Service. If you do not agree with all of these Terms of Service, then you are expressly prohibited from using the services and you must discontinue use immediately.</p>
                <h3 className="text-[20px] font-bold text-white mt-6 mb-2">2. Intellectual Property Rights</h3>
                <p>Unless otherwise indicated, the services and all source code, databases, functionality, software, website designs, audio, video, text, photographs, and graphics on the services (collectively, the “Content”) and the trademarks, service marks, and logos contained therein (the “Marks”) are owned or controlled by us or licensed to us.</p>
                <h3 className="text-[20px] font-bold text-white mt-6 mb-2">3. User Representations</h3>
                <p>By using the services, you represent and warrant that: (1) all registration information you submit will be true, accurate, current, and complete; (2) you will maintain the accuracy of such information and promptly update such registration information as necessary.</p>
                <h3 className="text-[20px] font-bold text-white mt-6 mb-2">4. Prohibited Activities</h3>
                <p>You may not access or use the services for any purpose other than that for which we make the services available. The services may not be used in connection with any commercial endeavors except those that are specifically endorsed or approved by us.</p>
                <h3 className="text-[20px] font-bold text-white mt-6 mb-2">5. Modifications and Interruptions</h3>
                <p>We reserve the right to change, modify, or remove the contents of the services at any time or for any reason at our sole discretion without notice. However, we have no obligation to update any information on our services.</p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
    </I18nContext.Provider>
  );
}
